// Case Buddy flow visual helpers.
// This module adapts Tool87 V13 visualization behavior for the Case Buddy case workspace:
// Main Data and the reviewed address legend are the inputs; visual output remains a
// deliverable/review artifact and does not replace spreadsheet-shaped case rows.
import { WORKFLOW_SIGNAL_CASE_FLOW_VISUALS_CREATED } from './CaseBuddyAppConstants.js';
import {
  caseBuddyHashString,
  firstNonEmptyEntryValue,
  normalizeCell,
  safeDateLabel
} from './CaseBuddyShared.js';
import { normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';
import { parseCaseEntries } from './CaseBuddySheetData.js';
import {
  CASE_TRACE_DIRECTION_FORWARD,
  CASE_TRACE_DIRECTION_REVERSE,
  buildCaseTraceDirectionContextByRowIndex,
  getCaseTraceDirectionForEntry,
  getCaseTraceDirectionRelevantAttribution,
  normalizeCaseTraceDirection
} from './CaseBuddyTraceDirection.js';

export const CASE_FLOW_VISUALS_RENDERER_VERSION = 'case_flow_visuals_tool87_v13_adapter_web';
export const CASE_FLOW_VISUALS_DEFAULT_OPTIONS = Object.freeze({
  layoutMode: 'balanced',
  mergeAddressMode: 'sameColumnMiddle',
  mergeTransactionNodes: true,
  wrapLinearTrunks: true,
  trunkWrapMinLength: 5,
  trunkWrapFirstRowLength: 5,
  trunkWrapOtherRowLength: 4,
  trunkWrapRowVerticalMultiplier: 1.35,
  highlightSwaps: true,
  highlightBridges: true,
  cleanupOverlaps: true,
  labelAddresses: true,
  showFundsFollowedBars: false
});

export const CASE_INVESTIGATION_VISUALS_DEFAULT_OPTIONS = Object.freeze({
  ...CASE_FLOW_VISUALS_DEFAULT_OPTIONS,
  layoutMode: 'upwardBranches',
  mergeAddressMode: 'none',
  mergeTransactionNodes: false,
  wrapLinearTrunks: false,
  highlightSwaps: false,
  highlightBridges: false,
  cleanupOverlaps: false,
  labelAddresses: true,
  showFundsFollowedBars: true
});

export const CASE_FLOW_VISUALS_TX_NODE_SIZE = Object.freeze({ width: 230, height: 126 });
export const CASE_FLOW_VISUALS_AMOUNT_NODE_SIZE = Object.freeze({ width: 146, height: 54 });
export const CASE_FLOW_VISUALS_ADDRESS_RADIUS = 34;

const CASE_FLOW_VISUALS_GRAPH_SETTINGS = Object.freeze({
  horizontalSpacing: 610,
  verticalSpacing: 250,
  addressHorizontalOffset: 360,
  addressVerticalOffset: 118,
  steppedUpwardDepthFactor: 0.7
});

const COL = Object.freeze({
  PATH_LABEL: 0,
  TX_HASH: 1,
  FROM_ADDRESS: 2,
  TO_ADDRESS: 3,
  DATE: 4,
  AMOUNT: 5,
  TOKEN: 6,
  NOTES: 9,
  ATTRIBUTION: 10,
  USER_NOTES: 11,
  FUNDS_FOLLOWED: 12,
  PRIOR_BALANCE: 13,
  FROM_HASH: 14,
  FROM_LABEL: 15,
  CONTINUES_AS: 16,
  LEFT_BOUNDS: 17,
  LEFT_BOUNDS_NUMERIC: 18,
  CHAIN_ID: 19,
  CHAIN_NAME: 20,
  HOP_LOGIC: 21,
  CASE_NOTES: 22,
  FROM_ATTRIBUTION: 23
});

function normalizeFlowOptions(options = {}) {
  return {
    ...CASE_FLOW_VISUALS_DEFAULT_OPTIONS,
    ...(options || {}),
    trunkWrapMinLength: Math.max(2, Number(options.trunkWrapMinLength) || CASE_FLOW_VISUALS_DEFAULT_OPTIONS.trunkWrapMinLength),
    trunkWrapFirstRowLength: Math.max(1, Number(options.trunkWrapFirstRowLength) || CASE_FLOW_VISUALS_DEFAULT_OPTIONS.trunkWrapFirstRowLength),
    trunkWrapOtherRowLength: Math.max(1, Number(options.trunkWrapOtherRowLength) || CASE_FLOW_VISUALS_DEFAULT_OPTIONS.trunkWrapOtherRowLength),
    trunkWrapRowVerticalMultiplier: Math.max(1, Number(options.trunkWrapRowVerticalMultiplier) || CASE_FLOW_VISUALS_DEFAULT_OPTIONS.trunkWrapRowVerticalMultiplier)
  };
}

function cell(row = [], index = 0) {
  const value = Array.isArray(row) ? row[index] : '';
  return value === null || value === undefined ? '' : String(value).trim();
}

function compactArray(values = []) {
  return (Array.isArray(values) ? values : [])
    .map(value => normalizeCell(value))
    .filter(Boolean);
}

function normalizeHash(value = '') {
  return String(value || '').trim().toLowerCase();
}

function normalizeLabel(value = '') {
  return String(value || '').trim().toLowerCase();
}

function normalizeAddress(value = '') {
  return String(value || '').trim().toLowerCase();
}

export function caseFlowVisualsShortenMiddle(value = '', left = 7, right = 5) {
  const text = normalizeCell(value);
  if (!text) return '';
  if (text.length <= left + right + 3) return text;
  return `${text.slice(0, left)}…${text.slice(-right)}`;
}

function truncateText(value = '', maxLength = 40) {
  const text = normalizeCell(value);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(1, maxLength - 1))}…`;
}

function wrapVisualLabel(text = '', maxChars = 24, maxLines = 3) {
  const value = normalizeCell(text);
  if (!value) return [];
  const words = value.split(/\s+/).filter(Boolean);
  const lines = [];
  let current = '';
  words.forEach((word) => {
    const attempt = current ? `${current} ${word}` : word;
    if (attempt.length > maxChars && current) {
      lines.push(current);
      current = word;
    } else {
      current = attempt;
    }
  });
  if (current) lines.push(current);
  if (lines.length <= maxLines) return lines;
  const trimmed = lines.slice(0, maxLines);
  trimmed[maxLines - 1] = `${trimmed[maxLines - 1].slice(0, Math.max(1, maxChars - 1))}…`;
  return trimmed;
}

function simpleHash(value = '') {
  const text = String(value || '');
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash << 5) - hash) + text.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

function parseNumber(value = '') {
  if (value === null || value === undefined) return null;
  const cleaned = String(value)
    .replace(/,/g, '')
    .match(/[-+]?(?:(?:\d+)(?:\.\d+)?|\.\d+)(?:e[-+]?\d+)?/i);
  if (!cleaned) return null;
  const parsed = Number(cleaned[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

function splitAddressCell(value = '') {
  if (!value) return [];
  return String(value)
    .split(/[\n,;]+/)
    .map(part => normalizeCell(part))
    .filter(Boolean);
}

function pushUniqueAddresses(target = [], addresses = []) {
  const existing = new Set(target.map(addr => normalizeAddress(addr)));
  (Array.isArray(addresses) ? addresses : []).forEach((addr) => {
    const key = normalizeAddress(addr);
    if (!key || existing.has(key)) return;
    existing.add(key);
    target.push(addr);
  });
}

function parseContinuesAs(value = '') {
  if (!value) return [];
  const text = String(value).trim();
  if (!text || /path\s+ends/i.test(text)) return [];
  let payload = text;
  const colonIndex = payload.indexOf(':');
  if (colonIndex >= 0) payload = payload.slice(colonIndex + 1);
  payload = payload.replace(/^continues\s+as\s+/i, '');
  return payload
    .split(/[,;\n]+/)
    .map(part => cleanContinuesToken(part))
    .filter(Boolean);
}

function cleanContinuesToken(token = '') {
  let cleaned = String(token || '').trim();
  cleaned = cleaned.replace(/^continues\s+as\s+/i, '').trim();
  cleaned = cleaned.replace(/[.。]+$/g, '').trim();
  const hashIndex = cleaned.search(/0x[a-fA-F0-9]+/);
  if (hashIndex >= 0) cleaned = cleaned.slice(hashIndex).trim();
  return cleaned;
}

function mergeAddressAndOutputData(entry = {}, row = []) {
  const froms = splitAddressCell(cell(row, COL.FROM_ADDRESS));
  const tos = splitAddressCell(cell(row, COL.TO_ADDRESS));
  pushUniqueAddresses(entry.fromAddresses, froms);
  pushUniqueAddresses(entry.toAddresses, tos);

  const rowAmount = cell(row, COL.AMOUNT);
  const rowToken = cell(row, COL.TOKEN);
  const rowFundsFollowed = cell(row, COL.FUNDS_FOLLOWED);
  const rowLeftBounds = cell(row, COL.LEFT_BOUNDS);
  const rowLeftBoundsNumeric = cell(row, COL.LEFT_BOUNDS_NUMERIC);

  tos.forEach((toAddress) => {
    entry.outputs.push({
      toAddress,
      amount: rowAmount || entry.amount || '',
      amountNumeric: parseNumber(rowAmount || entry.amount || ''),
      tokenType: rowToken || entry.tokenType || '',
      fundsFollowed: rowFundsFollowed || entry.fundsFollowed || '',
      leftBounds: rowLeftBounds || entry.leftBounds || '',
      leftBoundsNumeric: rowLeftBoundsNumeric || entry.leftBoundsNumeric || ''
    });
  });
}

function copyFirstNonEmpty(target = {}, key = '', value = '') {
  if (!target[key] && value) target[key] = value;
}

function normalizeVisualEntry(rawEntry = {}, index = 0, options = {}) {
  const rows = Array.isArray(rawEntry.rows) ? rawEntry.rows : [];
  const lead = rows[0] || { rowIndex: rawEntry.startRow || 0, row: [] };
  const leadRow = Array.isArray(lead.row) ? lead.row : [];
  const startRow = (Number(lead.rowIndex) || 0) + 1;
  const traceDirection = normalizeCaseTraceDirection(options.traceDirection)
    || getCaseTraceDirectionForEntry(rawEntry, CASE_TRACE_DIRECTION_FORWARD);
  const isReverse = traceDirection === CASE_TRACE_DIRECTION_REVERSE;
  const entry = {
    entryId: '',
    id: `entry-${startRow}-${index}`,
    originalOrder: index,
    startRow,
    rowIndex: Number(lead.rowIndex) || 0,
    spreadsheetRowNumber: startRow,
    rowNumbers: rows.map(rowRecord => (Number(rowRecord?.rowIndex) || 0) + 1),
    pathLabel: cell(leadRow, COL.PATH_LABEL) || firstNonEmptyEntryValue(rows, COL.PATH_LABEL),
    transactionHash: normalizeCell(rawEntry.hash || cell(leadRow, COL.TX_HASH) || firstNonEmptyEntryValue(rows, COL.TX_HASH)),
    hash: normalizeCell(rawEntry.hash || cell(leadRow, COL.TX_HASH) || firstNonEmptyEntryValue(rows, COL.TX_HASH)),
    fromAddresses: [],
    toAddresses: [],
    outputs: [],
    transactionDateRaw: cell(leadRow, COL.DATE) || firstNonEmptyEntryValue(rows, COL.DATE),
    amount: cell(leadRow, COL.AMOUNT) || firstNonEmptyEntryValue(rows, COL.AMOUNT),
    amountNumeric: parseNumber(cell(leadRow, COL.AMOUNT) || firstNonEmptyEntryValue(rows, COL.AMOUNT)),
    tokenType: cell(leadRow, COL.TOKEN) || firstNonEmptyEntryValue(rows, COL.TOKEN),
    note: cell(leadRow, COL.NOTES) || firstNonEmptyEntryValue(rows, COL.NOTES),
    systemNotes: cell(leadRow, COL.NOTES) || firstNonEmptyEntryValue(rows, COL.NOTES),
    attribution: getCaseTraceDirectionRelevantAttribution(leadRow, traceDirection),
    ...(isReverse
      ? {
        toAttribution: cell(leadRow, COL.ATTRIBUTION) || firstNonEmptyEntryValue(rows, COL.ATTRIBUTION),
        fromAttribution: cell(leadRow, COL.FROM_ATTRIBUTION) || firstNonEmptyEntryValue(rows, COL.FROM_ATTRIBUTION),
        caseNotes: cell(leadRow, COL.CASE_NOTES) || firstNonEmptyEntryValue(rows, COL.CASE_NOTES),
        traceDirection: CASE_TRACE_DIRECTION_REVERSE
      }
      : {}),
    userNotes: cell(leadRow, COL.USER_NOTES) || firstNonEmptyEntryValue(rows, COL.USER_NOTES),
    analystNotes: cell(leadRow, COL.USER_NOTES) || firstNonEmptyEntryValue(rows, COL.USER_NOTES),
    fundsFollowed: cell(leadRow, COL.FUNDS_FOLLOWED) || firstNonEmptyEntryValue(rows, COL.FUNDS_FOLLOWED),
    balance: cell(leadRow, COL.PRIOR_BALANCE) || firstNonEmptyEntryValue(rows, COL.PRIOR_BALANCE),
    fromHash: cell(leadRow, COL.FROM_HASH) || firstNonEmptyEntryValue(rows, COL.FROM_HASH),
    fromLabel: cell(leadRow, COL.FROM_LABEL) || firstNonEmptyEntryValue(rows, COL.FROM_LABEL),
    continuesAs: cell(leadRow, COL.CONTINUES_AS) || firstNonEmptyEntryValue(rows, COL.CONTINUES_AS),
    leftBounds: cell(leadRow, COL.LEFT_BOUNDS) || firstNonEmptyEntryValue(rows, COL.LEFT_BOUNDS),
    leftBoundsNumeric: cell(leadRow, COL.LEFT_BOUNDS_NUMERIC) || firstNonEmptyEntryValue(rows, COL.LEFT_BOUNDS_NUMERIC),
    chainId: cell(leadRow, COL.CHAIN_ID) || firstNonEmptyEntryValue(rows, COL.CHAIN_ID),
    chainName: cell(leadRow, COL.CHAIN_NAME) || firstNonEmptyEntryValue(rows, COL.CHAIN_NAME),
    hopLogic: cell(leadRow, COL.HOP_LOGIC) || firstNonEmptyEntryValue(rows, COL.HOP_LOGIC),
    rows
  };

  rows.forEach((rowRecord) => {
    const row = Array.isArray(rowRecord?.row) ? rowRecord.row : [];
    copyFirstNonEmpty(entry, 'pathLabel', cell(row, COL.PATH_LABEL));
    copyFirstNonEmpty(entry, 'transactionDateRaw', cell(row, COL.DATE));
    copyFirstNonEmpty(entry, 'amount', cell(row, COL.AMOUNT));
    copyFirstNonEmpty(entry, 'tokenType', cell(row, COL.TOKEN));
    copyFirstNonEmpty(entry, 'note', cell(row, COL.NOTES));
    copyFirstNonEmpty(entry, 'systemNotes', cell(row, COL.NOTES));
    copyFirstNonEmpty(entry, 'attribution', getCaseTraceDirectionRelevantAttribution(row, traceDirection));
    if (isReverse) {
      copyFirstNonEmpty(entry, 'toAttribution', cell(row, COL.ATTRIBUTION));
      copyFirstNonEmpty(entry, 'fromAttribution', cell(row, COL.FROM_ATTRIBUTION));
      copyFirstNonEmpty(entry, 'caseNotes', cell(row, COL.CASE_NOTES));
    }
    copyFirstNonEmpty(entry, 'userNotes', cell(row, COL.USER_NOTES));
    copyFirstNonEmpty(entry, 'analystNotes', cell(row, COL.USER_NOTES));
    copyFirstNonEmpty(entry, 'fundsFollowed', cell(row, COL.FUNDS_FOLLOWED));
    copyFirstNonEmpty(entry, 'balance', cell(row, COL.PRIOR_BALANCE));
    copyFirstNonEmpty(entry, 'fromHash', cell(row, COL.FROM_HASH));
    copyFirstNonEmpty(entry, 'fromLabel', cell(row, COL.FROM_LABEL));
    copyFirstNonEmpty(entry, 'continuesAs', cell(row, COL.CONTINUES_AS));
    copyFirstNonEmpty(entry, 'leftBounds', cell(row, COL.LEFT_BOUNDS));
    copyFirstNonEmpty(entry, 'leftBoundsNumeric', cell(row, COL.LEFT_BOUNDS_NUMERIC));
    copyFirstNonEmpty(entry, 'chainId', cell(row, COL.CHAIN_ID));
    copyFirstNonEmpty(entry, 'chainName', cell(row, COL.CHAIN_NAME));
    copyFirstNonEmpty(entry, 'hopLogic', cell(row, COL.HOP_LOGIC));
    if (entry.amountNumeric === null) entry.amountNumeric = parseNumber(cell(row, COL.AMOUNT));
    mergeAddressAndOutputData(entry, row);
  });

  entry.normalizedHash = normalizeHash(entry.transactionHash);
  entry.childHashes = parseContinuesAs(entry.continuesAs);
  entry.combinedText = compactArray([
    entry.pathLabel,
    entry.transactionHash,
    entry.note,
    entry.attribution,
    entry.userNotes,
    entry.continuesAs,
    entry.chainName,
    entry.fundsFollowed,
    ...(isReverse ? [entry.caseNotes, entry.fromAttribution] : []),
    ...(entry.rows || []).flatMap(rowRecord => [
      rowRecord?.row?.[COL.NOTES],
      rowRecord?.row?.[COL.ATTRIBUTION],
      rowRecord?.row?.[COL.USER_NOTES],
      rowRecord?.row?.[COL.CONTINUES_AS],
      ...(isReverse ? [rowRecord?.row?.[COL.CASE_NOTES], rowRecord?.row?.[COL.FROM_ATTRIBUTION]] : [])
    ])
  ]).join(' ');
  return entry;
}

function assignEntryIds(entries = []) {
  const seenIds = new Map();
  entries.forEach((entry) => {
    const baseId = entry.pathLabel || entry.transactionHash || `row-${entry.startRow}`;
    const count = seenIds.get(baseId) || 0;
    seenIds.set(baseId, count + 1);
    entry.entryId = count === 0 ? baseId : `${baseId}#${entry.startRow}`;
    entry.id = entry.entryId;
  });
  return entries;
}

function classifyAddressLabel(label = '') {
  const text = normalizeCell(label).toLowerCase();
  if (!text) return 'regular';
  const serviceKeywords = [
    'tokenlon', '0xprotocol', 'aeroswap', 'airswap', 'hifiswap', 'allbridge',
    'avalanche', 'bc.game', 'betpandacasino', 'betplay', 'bitget wallet bridge',
    'bkbridge', 'blast yield manager', 'bridge', 'bridgers', 'butterbridge',
    'chainflip', 'clash.gg', 'coinjoin', 'coinomize', 'cortex', 'curve swap',
    'cwallet', 'debridge', 'dex', 'gamdom', 'garden', 'gas.zip', 'hop protocol',
    'howl', 'hyperliquid', 'imtoken', 'jambler', 'joinmarket', 'layerzero',
    'li.fi', 'lightning network', 'luckyfish', 'mayan', 'metamask', 'mintify',
    'okx web 3', 'near-intents', 'pancakeswap', 'payment processor', 'pendle',
    'protocol', 'rango', 'roobet', 'router', 'seawise', 'shuffle', 'socket',
    'squid router', 'stake', 'stargate', 'sunswap', 'sushiswap', 'swft blockchain',
    'swap', 'switchain', 'thorchain', 'transit finance', 'transitswap', 'wanchain',
    'wasabi', 'wormhole', 'synapse', 'mixer', 'casino', 'yield', 'defi',
    'contract', 'smart contract', 'service', 'liquidity', 'pool', 'vault', 'dao',
    'marketplace', 'nft', 'merchant'
  ];
  if (serviceKeywords.some(word => text.includes(word))) return 'service';
  if (/\b(deposit address|deposit wallet|deposit account)\b/.test(text)) return 'exchange';
  const genericPrefixes = [
    "suspect's address", 'suspects address', 'suspect address', "victim's address",
    'victims address', 'victim address', 'address #', 'wallet address', 'personal wallet'
  ];
  if (genericPrefixes.some(prefix => text.startsWith(prefix))) return 'regular';
  const exchangeSignals = [
    'exchange', 'coinbase', 'binance', 'kraken', 'okx', 'kucoin', 'bybit', 'bitget',
    'bitgo', 'crypto.com', 'gemini', 'bitstamp', 'huobi', 'htx', 'mexc', 'gate.io',
    'upbit', 'bitfinex', 'bittrex', 'poloniex', 'coinlist', 'robinhood', 'cash app',
    'bitflyer', 'bitkub', 'bitmart', 'luno', 'bitso', 'coinsquare', 'shakepay',
    'ndax', 'newton', 'hot wallet'
  ];
  if (exchangeSignals.some(word => text.includes(word))) return 'exchange';
  return 'regular';
}

function computeAddressLabelFontSize(linesOrLabel = []) {
  const lines = Array.isArray(linesOrLabel) ? linesOrLabel : wrapVisualLabel(linesOrLabel, 24, 3);
  const longestLine = Math.max(...lines.map(line => String(line || '').length), 1);
  const maxSize = 18;
  const minSize = 13;
  const visualWidth = 270;
  const estimated = visualWidth / (longestLine * 0.62);
  return Math.round(Math.max(minSize, Math.min(maxSize, estimated)));
}

function makeAddressLabelMeta(label = '') {
  const cleanLabel = normalizeCell(label).replace(/\s+/g, ' ');
  const labelLines = wrapVisualLabel(cleanLabel, 24, 3);
  return {
    label: cleanLabel,
    category: classifyAddressLabel(cleanLabel),
    labelLines,
    labelFontSize: computeAddressLabelFontSize(labelLines)
  };
}

function buildAddressLabelMap(workflowState = {}) {
  const entries = Array.isArray(workflowState?.addressLegendReview?.entries)
    ? workflowState.addressLegendReview.entries
    : [];
  const map = new Map();
  entries.forEach((entry) => {
    const address = normalizeCell(entry.address || entry.Address || entry.preferredDisplayAddress || entry.PreferredDisplayAddress);
    const label = normalizeCell(entry.label || entry.Label || entry.proposedLegendLabel || entry.ProposedLegendLabel);
    const key = normalizeAddress(address);
    if (!key || !label) return;
    const meta = makeAddressLabelMeta(label);
    map.set(key, {
      address,
      ...meta,
      chainId: normalizeCell(entry.chainId || entry.ChainId),
      chainName: normalizeCell(entry.chainName || entry.ChainName),
      notes: normalizeCell(entry.notes || entry.Notes)
    });
  });
  return map;
}

function tryParseStandardNumericRoot(label = '') {
  const match = String(label || '').match(/^(\d+)/);
  if (!match) return { isValid: false, root: '', isMain: false };
  return {
    isValid: true,
    root: match[1],
    isMain: match[1].length === String(label).length
  };
}

function tryParseAlphabeticRoot(label = '') {
  const match = String(label || '').match(/^([A-Za-z]+)/);
  if (!match) return { isValid: false, root: '', isMain: false };
  return {
    isValid: true,
    root: match[1],
    isMain: match[1].length === String(label).length
  };
}

function tryParseFlowLabel(rawLabel = '') {
  const empty = { isValid: false, isMain: false, flowRoot: '', sortKind: 3 };
  const label = normalizeCell(rawLabel);
  if (!label) return empty;
  const hyphenIndex = label.indexOf('-');
  if (hyphenIndex > 0 && hyphenIndex < label.length - 1) {
    const prefix = label.slice(0, hyphenIndex).trim();
    const suffix = label.slice(hyphenIndex + 1).trim();
    const numeric = tryParseStandardNumericRoot(suffix);
    if (prefix && numeric.isValid) {
      return {
        isValid: true,
        isMain: numeric.isMain,
        flowRoot: `${prefix}-${numeric.root}`,
        sortKind: 2,
        prefixPart: prefix,
        prefixedNumericRoot: Number.parseInt(numeric.root, 10) || Number.MAX_SAFE_INTEGER
      };
    }
  }
  const standard = tryParseStandardNumericRoot(label);
  if (standard.isValid) {
    return {
      isValid: true,
      isMain: standard.isMain,
      flowRoot: standard.root,
      sortKind: 0,
      numericRoot: Number.parseInt(standard.root, 10) || Number.MAX_SAFE_INTEGER
    };
  }
  const alpha = tryParseAlphabeticRoot(label);
  if (alpha.isValid) {
    return {
      isValid: true,
      isMain: alpha.isMain,
      flowRoot: alpha.root,
      sortKind: 1,
      alphaRoot: alpha.root
    };
  }
  return empty;
}

function getFlowSortInfo(mainLabel = '') {
  const parsed = tryParseFlowLabel(mainLabel);
  if (parsed.isValid) return parsed;
  return { sortKind: 3, flowRoot: mainLabel || '' };
}

function compareFlowSortInfo(a = {}, b = {}) {
  const ak = a || { sortKind: 3 };
  const bk = b || { sortKind: 3 };
  if (ak.sortKind !== bk.sortKind) return ak.sortKind - bk.sortKind;
  if (ak.sortKind === 0) return (ak.numericRoot || 0) - (bk.numericRoot || 0);
  if (ak.sortKind === 1) return String(ak.alphaRoot || '').localeCompare(String(bk.alphaRoot || ''));
  if (ak.sortKind === 2) {
    const prefixCompare = String(ak.prefixPart || '').localeCompare(String(bk.prefixPart || ''));
    if (prefixCompare !== 0) return prefixCompare;
    return (ak.prefixedNumericRoot || 0) - (bk.prefixedNumericRoot || 0);
  }
  return String(ak.flowRoot || '').localeCompare(String(bk.flowRoot || ''));
}

function buildFlowsFromEntries(entries = []) {
  const warnings = [];
  const map = new Map();
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const parsed = tryParseFlowLabel(entry.pathLabel);
    if (!parsed.isValid) {
      warnings.push(`Entry row ${entry.startRow} has an unrecognized path label '${entry.pathLabel || '(blank)'}' and was not assigned to a main flow.`);
      return;
    }
    if (!map.has(parsed.flowRoot)) {
      map.set(parsed.flowRoot, {
        id: `flow-${simpleHash(parsed.flowRoot)}`,
        key: parsed.flowRoot,
        mainLabel: parsed.flowRoot,
        mainTxn: null,
        mainHash: '',
        subTransactions: [],
        transactions: [],
        entries: [],
        sortInfo: getFlowSortInfo(parsed.flowRoot),
        firstRow: entry.startRow
      });
    }
    const flow = map.get(parsed.flowRoot);
    flow.transactions.push(entry);
    flow.entries.push(entry);
    flow.firstRow = Math.min(flow.firstRow || entry.startRow, entry.startRow);
    if (parsed.isMain) {
      if (!flow.mainTxn) flow.mainTxn = entry;
      else flow.subTransactions.push(entry);
    } else {
      flow.subTransactions.push(entry);
    }
  });

  const flows = Array.from(map.values()).map((flow) => {
    if (!flow.mainTxn) {
      const noFromHash = flow.transactions.find(tx => !tx.fromHash);
      flow.mainTxn = noFromHash || flow.transactions[0] || null;
    }
    if (flow.mainTxn) {
      flow.mainHash = flow.mainTxn.transactionHash || flow.mainTxn.hash || '';
      const mainId = flow.mainTxn.entryId;
      flow.subTransactions = flow.transactions.filter(tx => tx.entryId !== mainId);
    }
    flow.transactionCount = flow.transactions.length;
    return flow;
  });

  flows.sort((a, b) => compareFlowSortInfo(a.sortInfo, b.sortInfo));
  return { flows, warnings };
}

export function buildCaseFlowVisualsState(mainData = [], workflowState = {}, options = {}) {
  const matrix = normalizeCaseMatrix(mainData);
  const traceDirectionContextByRowIndex = buildCaseTraceDirectionContextByRowIndex(matrix);
  const entries = assignEntryIds(parseCaseEntries(matrix)
    .map((entry, index) => normalizeVisualEntry(entry, index, {
      traceDirection: traceDirectionContextByRowIndex.get(entry.startRow)?.traceDirection
    }))
    .filter(entry => entry.transactionHash || entry.fromAddresses.length || entry.toAddresses.length));
  const flowResult = buildFlowsFromEntries(entries);
  const flows = flowResult.flows;
  const addressLabelMap = buildAddressLabelMap(workflowState);
  const revision = Number(options.caseDataRevision) || 0;
  const signals = workflowState?.signalCompleteByCode || {};
  const signalRecord = signals[WORKFLOW_SIGNAL_CASE_FLOW_VISUALS_CREATED] || null;
  const manualRecord = workflowState?.manualCompleteByTag?.CASE_FLOW_VISUALS || null;
  const signalCurrent = !!signalRecord?.completedAtUtc && Number(signalRecord.revision) === revision;
  const manualCurrent = !!manualRecord?.completedAtUtc;
  const reviewIsCurrent = signalCurrent || manualCurrent;
  const completedAtUtc = signalRecord?.completedAtUtc || manualRecord?.completedAtUtc || '';
  const source = signalCurrent ? 'signal' : manualCurrent ? 'manual' : '';
  const legendReview = workflowState?.addressLegendReview || {};
  const currentBasisHash = `case-flow-visuals-${caseBuddyHashString(JSON.stringify({
    renderer: CASE_FLOW_VISUALS_RENDERER_VERSION,
    caseId: normalizeCell(options.caseDisplayId || options.caseId),
    caseName: normalizeCell(options.caseDisplayName || options.caseName),
    revision,
    entryCount: entries.length,
    flowKeys: flows.map(flow => `${flow.mainLabel}|${flow.mainHash}|${flow.entries.length}`),
    legendBasis: normalizeCell(legendReview.reviewedBasisHash || legendReview.confirmedReviewBasisHash || legendReview.builtBasisHash),
    options: normalizeFlowOptions(options.visualOptions || options)
  }))}`;
  const rows = buildCaseFlowVisualsPackageRows({ flows, entries, addressLabelCount: addressLabelMap.size }, {
    caseDisplayName: options.caseDisplayName,
    caseDisplayId: options.caseDisplayId,
    caseDataRevision: revision,
    reviewIsCurrent,
    completedAtUtc,
    source,
    currentBasisHash
  });

  return {
    rendererVersion: CASE_FLOW_VISUALS_RENDERER_VERSION,
    currentBasisHash,
    caseDataRevision: revision,
    entries,
    flows,
    warnings: flowResult.warnings,
    flowCount: flows.length,
    entryCount: entries.length,
    addressLabelCount: addressLabelMap.size,
    addressLabelMap,
    canBuild: flows.length > 0,
    reviewIsCurrent,
    signalCurrent,
    manualCurrent,
    completedAtUtc,
    source,
    rows,
    summary: flows.length === 0
      ? 'No transaction flows are available yet. Add Main Data rows with transaction hashes and Case Buddy path labels before building visuals.'
      : reviewIsCurrent
        ? `Case flow visuals are marked complete${completedAtUtc ? ` (${safeDateLabel(completedAtUtc)})` : ''}.`
        : `${flows.length} case flow visual${flows.length === 1 ? '' : 's'} can be previewed and exported from Main Data with reviewed address labels applied when available.`
  };
}

export function buildCaseFlowVisualsPackageRows(stateOrSummary = {}, options = {}) {
  const flows = Array.isArray(stateOrSummary.flows) ? stateOrSummary.flows : [];
  const entries = Array.isArray(stateOrSummary.entries) ? stateOrSummary.entries : [];
  const addressLabelCount = Number(stateOrSummary.addressLabelCount) || 0;
  const rows = [
    ['Case Buddy Flow Visuals Package', options.caseDisplayName || '', '', '', '', '', '', '', '', 'Tool87 V13-style in-app visual package. Main Data is not modified.'],
    ['Case ID', options.caseDisplayId || '', 'Revision', options.caseDataRevision || 0, 'Renderer', CASE_FLOW_VISUALS_RENDERER_VERSION, 'Marked Complete', options.reviewIsCurrent ? 'Yes' : 'No'],
    ['Basis Hash', options.currentBasisHash || '', 'Completed At', options.completedAtUtc || '', 'Source', options.source || '', 'Address Labels', addressLabelCount],
    [],
    ['Package Summary'],
    ['Main Flow Visuals', flows.length, 'Transaction Entries Parsed', entries.length, 'Address Labels Available', addressLabelCount, 'Recommended Setup', 'Enabled by default'],
    [],
    ['Recommended Visual Setup'],
    ['Feature', 'Default'],
    ['Address labels', 'Applied from the reviewed Case Buddy address legend when available'],
    ['Swap highlighting', 'Enabled from swap/dex/router language in notes/attribution'],
    ['Bridge highlighting', 'Enabled from bridge/stargate/wormhole/layer language in notes/attribution'],
    ['WayFinder column merge', 'Same-address nodes in the same between-transaction column are merged'],
    ['Repeated transaction merge', 'Repeated transaction hashes are merged inside each flow visual'],
    ['Linear trunk wrapping', 'Enabled with Tool87 V13 geometry and a default 5-node minimum'],
    ['Overlap cleanup', 'Enabled after layout and merge'],
    [],
    ['Flow', 'Root Hash', 'Entries', 'First Row']
  ];
  flows.forEach((flow, index) => {
    rows.push([
      flow.mainLabel || `Flow ${index + 1}`,
      flow.mainHash || '',
      flow.entries?.length || flow.transactionCount || 0,
      flow.firstRow || ''
    ]);
  });
  return rows;
}

function addToMultiMap(map, key, value) {
  if (!key) return;
  if (!map.has(key)) map.set(key, []);
  map.get(key).push(value);
}

function compareGraphNodesBasic(a = {}, b = {}) {
  const ad = Date.parse(a.tx?.transactionDateRaw || '') || Number.MAX_SAFE_INTEGER;
  const bd = Date.parse(b.tx?.transactionDateRaw || '') || Number.MAX_SAFE_INTEGER;
  if (ad !== bd) return ad - bd;
  const labelCompare = String(a.tx?.pathLabel || '').localeCompare(String(b.tx?.pathLabel || ''), undefined, { numeric: true, sensitivity: 'base' });
  if (labelCompare !== 0) return labelCompare;
  return String(a.tx?.transactionHash || '').localeCompare(String(b.tx?.transactionHash || ''));
}

function computeParentBarycenter(node = {}) {
  if (!node.parents || node.parents.length === 0) return Number.MAX_SAFE_INTEGER;
  return node.parents.reduce((sum, parent) => sum + (parent.order || 0), 0) / node.parents.length;
}

function assignDepths(roots = [], nodes = []) {
  const queue = [];
  const seen = new Set();
  roots.forEach((root) => {
    root.depth = 0;
    queue.push(root);
    seen.add(root.id);
  });
  while (queue.length) {
    const node = queue.shift();
    node.children.forEach((child) => {
      const candidateDepth = node.depth + 1;
      if (!seen.has(child.id)) {
        child.depth = candidateDepth;
        seen.add(child.id);
        queue.push(child);
      } else if (candidateDepth < child.depth) {
        child.depth = candidateDepth;
      }
    });
  }
  nodes.forEach((node) => {
    if (!seen.has(node.id)) node.depth = 0;
  });
}

function assignVerticalOrders(nodes = []) {
  const byDepth = new Map();
  nodes.forEach((node) => {
    if (!byDepth.has(node.depth)) byDepth.set(node.depth, []);
    byDepth.get(node.depth).push(node);
  });
  const depths = Array.from(byDepth.keys()).sort((a, b) => a - b);
  depths.forEach((depth) => {
    const list = byDepth.get(depth);
    if (depth === 0) {
      list.sort(compareGraphNodesBasic);
    } else {
      list.sort((a, b) => {
        const ac = computeParentBarycenter(a);
        const bc = computeParentBarycenter(b);
        if (ac !== bc) return ac - bc;
        return compareGraphNodesBasic(a, b);
      });
    }
    list.forEach((node, index) => {
      node.order = index;
    });
  });
}

function buildTransactionGraph(txs = [], mainTxn = null, options = {}) {
  const nodes = [];
  const warnings = [];
  const byEntryId = new Map();
  const hashToNodes = new Map();
  const labelToNodes = new Map();
  (Array.isArray(txs) ? txs : []).forEach((tx) => {
    const id = tx.entryId || tx.pathLabel || tx.transactionHash || `row-${tx.startRow}`;
    const node = { id, tx, children: [], parents: [], depth: 0, order: 0 };
    nodes.push(node);
    byEntryId.set(id, node);
    addToMultiMap(hashToNodes, normalizeHash(tx.transactionHash), node);
    addToMultiMap(labelToNodes, normalizeLabel(tx.pathLabel), node);
  });

  const resolveToken = (token, contextNode, desiredDirection) => {
    const clean = cleanContinuesToken(token);
    if (!clean) return null;
    const labelMatches = labelToNodes.get(normalizeLabel(clean)) || [];
    if (labelMatches.length === 1) return labelMatches[0];
    const hashMatches = hashToNodes.get(normalizeHash(clean)) || [];
    if (hashMatches.length === 1) return hashMatches[0];
    if (hashMatches.length > 1 && contextNode) {
      const narrowed = hashMatches.filter((candidate) => {
        if (desiredDirection === 'child') return normalizeHash(candidate.tx.fromHash) === normalizeHash(contextNode.tx.transactionHash);
        if (desiredDirection === 'parent') return parseContinuesAs(candidate.tx.continuesAs).some(part => normalizeHash(part) === normalizeHash(contextNode.tx.transactionHash));
        return false;
      });
      if (narrowed.length === 1) return narrowed[0];
    }
    if (hashMatches.length > 1 && options.mergeTransactionNodes) return hashMatches[0];
    if (labelMatches.length > 1 || hashMatches.length > 1) warnings.push(`Token '${clean}' was ambiguous in flow; no link was created for that token.`);
    return null;
  };

  const linkNodes = (parent, child) => {
    if (!parent || !child || parent === child) return;
    if (!parent.children.includes(child)) parent.children.push(child);
    if (!child.parents.includes(parent)) child.parents.push(parent);
  };

  nodes.forEach((node) => {
    parseContinuesAs(node.tx.continuesAs).forEach((token) => {
      const child = resolveToken(token, node, 'child');
      if (child) linkNodes(node, child);
    });
  });

  nodes.forEach((node) => {
    const fromHash = normalizeHash(node.tx.fromHash);
    const fromLabel = normalizeLabel(node.tx.fromLabel);
    let parent = null;
    if (fromHash) {
      const matches = hashToNodes.get(fromHash) || [];
      if (matches.length === 1) parent = matches[0];
      else if (matches.length > 1) {
        const narrowed = matches.filter(candidate => parseContinuesAs(candidate.tx.continuesAs)
          .some(part => normalizeHash(part) === normalizeHash(node.tx.transactionHash)
            || normalizeLabel(part) === normalizeLabel(node.tx.pathLabel)));
        if (narrowed.length === 1) parent = narrowed[0];
        else if (options.mergeTransactionNodes) parent = matches[0];
        else warnings.push(`Row ${node.tx.startRow} from hash '${node.tx.fromHash}' matched multiple entries. The parent link was skipped unless Continues As already linked it.`);
      }
    }
    if (!parent && fromLabel) {
      const matches = labelToNodes.get(fromLabel) || [];
      if (matches.length === 1) parent = matches[0];
      else if (matches.length > 1) warnings.push(`Row ${node.tx.startRow} from label '${node.tx.fromLabel}' matched multiple entries. The parent link was skipped.`);
    }
    if (parent) linkNodes(parent, node);
  });

  let roots = nodes.filter(node => node.parents.length === 0);
  const mainNode = mainTxn ? byEntryId.get(mainTxn.entryId) : null;
  if (mainNode && !roots.includes(mainNode) && !mainNode.tx.fromHash) roots.unshift(mainNode);
  if (roots.length === 0 && mainNode) roots = [mainNode];
  if (roots.length === 0 && nodes.length > 0) roots = [nodes[0]];
  assignDepths(roots, nodes);
  assignVerticalOrders(nodes);
  return { nodes, roots, warnings };
}

function computeUpwardSlots(count = 0) {
  if (count <= 0) return [];
  if (count === 1) return [0];
  const centerIndex = (count - 1) / 2;
  const ranked = Array.from({ length: count }, (_, index) => {
    const signed = index - centerIndex;
    return { index, primary: Math.abs(signed), secondary: signed };
  });
  ranked.sort((a, b) => {
    if (a.primary !== b.primary) return a.primary - b.primary;
    return a.secondary - b.secondary;
  });
  const slots = Array(count).fill(0);
  ranked.forEach((item, rank) => { slots[item.index] = rank; });
  return slots;
}

function computeTransactionPositions(graphNodes = [], options = {}) {
  const positions = {};
  const byDepth = new Map();
  graphNodes.forEach((node) => {
    if (!byDepth.has(node.depth)) byDepth.set(node.depth, []);
    byDepth.get(node.depth).push(node);
  });
  byDepth.forEach(list => list.sort((a, b) => a.order - b.order));
  Array.from(byDepth.keys()).forEach((depth) => {
    const list = byDepth.get(depth) || [];
    const count = list.length;
    const centerIndex = (count - 1) / 2;
    const upwardSlots = computeUpwardSlots(count);
    list.forEach((node, index) => {
      const x = depth * CASE_FLOW_VISUALS_GRAPH_SETTINGS.horizontalSpacing;
      let y = 0;
      if (options.layoutMode === 'upwardBranches') {
        y = -upwardSlots[index] * CASE_FLOW_VISUALS_GRAPH_SETTINGS.verticalSpacing;
      } else if (options.layoutMode === 'steppedUpward') {
        const depthBase = -depth * (CASE_FLOW_VISUALS_GRAPH_SETTINGS.verticalSpacing + 300) * CASE_FLOW_VISUALS_GRAPH_SETTINGS.steppedUpwardDepthFactor;
        const dyWithinLayer = (index - centerIndex) * (CASE_FLOW_VISUALS_GRAPH_SETTINGS.verticalSpacing + 300) * 0.5;
        y = depthBase + dyWithinLayer;
      } else {
        y = (index - centerIndex) * CASE_FLOW_VISUALS_GRAPH_SETTINGS.verticalSpacing;
      }
      positions[node.id] = { x, y };
    });
  });
  return positions;
}

function buildLinearTrunkFromRoot(root = null) {
  const chain = [];
  const seen = new Set();
  let current = root;
  while (current && !seen.has(current.id)) {
    chain.push(current);
    seen.add(current.id);
    const children = current.children || [];
    if (children.length !== 1) break;
    const next = children[0];
    if (!next || (next.parents && next.parents.length > 1)) break;
    current = next;
  }
  return chain;
}

function collectGraphDescendants(startNodes = [], excludedIds = new Set()) {
  const result = [];
  const seen = new Set();
  const queue = [...(startNodes || [])].filter(Boolean);
  while (queue.length) {
    const node = queue.shift();
    if (!node || excludedIds.has(node.id) || seen.has(node.id)) continue;
    seen.add(node.id);
    result.push(node);
    (node.children || []).forEach((child) => {
      if (child && !seen.has(child.id) && !excludedIds.has(child.id)) queue.push(child);
    });
  }
  result.sort((a, b) => {
    if ((a.depth || 0) !== (b.depth || 0)) return (a.depth || 0) - (b.depth || 0);
    return (a.order || 0) - (b.order || 0);
  });
  return result;
}

function shiftDownstreamBranchesBelowWrappedTrunk(chain = [], chainIds = new Set(), positions = {}, layoutDirections = {}, rowInfos = []) {
  if (!chain.length || !rowInfos.length) return;
  const terminal = chain[chain.length - 1];
  const branchRoots = (terminal.children || []).filter(child => child && !chainIds.has(child.id));
  const branchNodes = collectGraphDescendants(branchRoots, chainIds);
  if (!branchNodes.length) return;
  const lastRow = rowInfos[rowInfos.length - 1];
  const branchDirection = lastRow.direction || 1;
  const minDepth = Math.min(...branchNodes.map(node => node.depth || 0));
  const originalYs = branchNodes.map(node => (positions[node.id] ? positions[node.id].y : 0));
  const originalMinY = Math.min(...originalYs);
  const maxWrappedY = Math.max(...rowInfos.map(row => row.y));
  const branchStartY = maxWrappedY + CASE_FLOW_VISUALS_GRAPH_SETTINGS.verticalSpacing * 1.75;
  const branchBaseX = lastRow.terminalX + branchDirection * Math.max(
    CASE_FLOW_VISUALS_GRAPH_SETTINGS.horizontalSpacing - CASE_FLOW_VISUALS_GRAPH_SETTINGS.addressHorizontalOffset,
    CASE_FLOW_VISUALS_GRAPH_SETTINGS.addressHorizontalOffset * 0.5
  );
  branchNodes.forEach((node) => {
    const original = positions[node.id] || { x: 0, y: originalMinY };
    const depthOffset = Math.max(0, (node.depth || 0) - minDepth);
    positions[node.id] = {
      x: branchBaseX + branchDirection * depthOffset * CASE_FLOW_VISUALS_GRAPH_SETTINGS.horizontalSpacing,
      y: branchStartY + (original.y - originalMinY)
    };
    layoutDirections[node.id] = branchDirection;
  });
}

function applyTrunkWrapIfEnabled(graph = {}, positions = {}, layoutDirections = {}, options = {}) {
  const stats = { wrappedTrunkLength: 0 };
  if (!options.wrapLinearTrunks || !graph || !Array.isArray(graph.nodes) || graph.nodes.length === 0) return stats;
  const roots = (graph.roots && graph.roots.length ? graph.roots : graph.nodes.filter(node => !node.parents || node.parents.length === 0))
    .filter(node => node && positions[node.id]);
  if (!roots.length) return stats;
  let bestPath = [];
  roots.forEach((root) => {
    const path = buildLinearTrunkFromRoot(root);
    if (path.length > bestPath.length) bestPath = path;
  });
  const minimum = Math.max(2, Number(options.trunkWrapMinLength) || 5);
  if (bestPath.length < minimum) return stats;

  const firstRowLength = Math.max(1, Number(options.trunkWrapFirstRowLength) || 5);
  const otherRowLength = Math.max(1, Number(options.trunkWrapOtherRowLength) || 4);
  const horizontalSpacing = CASE_FLOW_VISUALS_GRAPH_SETTINGS.horizontalSpacing;
  const rowSpacing = CASE_FLOW_VISUALS_GRAPH_SETTINGS.verticalSpacing * Math.max(1, Number(options.trunkWrapRowVerticalMultiplier) || 1.35);
  const anchor = { ...(positions[bestPath[0].id] || { x: 0, y: 0 }) };
  const chainIds = new Set(bestPath.map(node => node.id));
  const rowInfos = [];
  let index = 0;
  let rowIndex = 0;
  let previousTerminalX = anchor.x;
  while (index < bestPath.length) {
    const cap = rowIndex === 0 ? firstRowLength : otherRowLength;
    const rowCount = Math.min(cap, bestPath.length - index);
    const leftToRight = rowIndex % 2 === 0;
    let rowStartX = anchor.x;
    if (rowIndex > 0) {
      rowStartX = leftToRight
        ? previousTerminalX
        : previousTerminalX - (rowCount - 1) * horizontalSpacing;
    }
    const y = anchor.y + rowIndex * rowSpacing;
    const direction = leftToRight ? 1 : -1;
    for (let i = 0; i < rowCount; i += 1) {
      const node = bestPath[index + i];
      const x = leftToRight
        ? rowStartX + i * horizontalSpacing
        : rowStartX + (rowCount - 1 - i) * horizontalSpacing;
      positions[node.id] = { x, y };
      layoutDirections[node.id] = direction;
    }
    const lastNode = bestPath[index + rowCount - 1];
    const lastPos = positions[lastNode.id];
    previousTerminalX = lastPos.x + direction * CASE_FLOW_VISUALS_GRAPH_SETTINGS.addressHorizontalOffset;
    rowInfos.push({ rowIndex, y, direction, terminalX: previousTerminalX, lastNode });
    index += rowCount;
    rowIndex += 1;
  }
  shiftDownstreamBranchesBelowWrappedTrunk(bestPath, chainIds, positions, layoutDirections, rowInfos);
  stats.wrappedTrunkLength = bestPath.length;
  return stats;
}

function pickPrimaryGraphNodeForTransactionGroup(graphNodes = []) {
  const list = [...(graphNodes || [])];
  list.sort((a, b) => {
    const aMainLike = a.tx && !a.tx.fromHash ? 0 : 1;
    const bMainLike = b.tx && !b.tx.fromHash ? 0 : 1;
    if (aMainLike !== bMainLike) return aMainLike - bMainLike;
    return compareGraphNodesBasic(a, b);
  });
  return list[0] || graphNodes[0];
}

function computeTransactionGroupLayoutDirection(graphNodes = [], layoutDirections = {}) {
  const list = Array.isArray(graphNodes) ? graphNodes : [];
  if (!list.length) return 1;
  const primary = pickPrimaryGraphNodeForTransactionGroup(list);
  const primaryDirection = primary && Number(layoutDirections[primary.id]);
  if (primaryDirection < 0) return -1;
  if (primaryDirection > 0) return 1;
  const signedTotal = list.reduce((sum, graphNode) => {
    const direction = Number(layoutDirections[graphNode.id]);
    if (direction < 0) return sum - 1;
    if (direction > 0) return sum + 1;
    return sum;
  }, 0);
  return signedTotal < 0 ? -1 : 1;
}

function transactionVisualGroupKey(graphNode = {}, options = {}) {
  if (!options.mergeTransactionNodes) return `entry:${graphNode.id}`;
  const normalizedHash = normalizeHash(graphNode.tx && graphNode.tx.transactionHash);
  if (!normalizedHash) return `entry:${graphNode.id}`;
  return `hash:${normalizedHash}`;
}

function combinePathLabelsForMergedTransaction(graphNodes = []) {
  const labels = [];
  const seen = new Set();
  (graphNodes || []).forEach((graphNode) => {
    const label = normalizeCell(graphNode.tx && graphNode.tx.pathLabel);
    if (!label) return;
    const key = normalizeLabel(label);
    if (seen.has(key)) return;
    seen.add(key);
    labels.push(label);
  });
  if (labels.length === 0) return '(no label)';
  const combined = labels.join(' / ');
  return truncateText(combined, labels.length > 1 ? 70 : 44);
}

function firstNonEmpty(values = []) {
  return (values || []).find(value => normalizeCell(value)) || '';
}

function computeTransactionGroupVisualDepth(graphNodes = [], fallbackX = 0) {
  if (!graphNodes || graphNodes.length === 0) return Math.round((fallbackX || 0) / CASE_FLOW_VISUALS_GRAPH_SETTINGS.horizontalSpacing);
  const averageDepth = graphNodes.reduce((sum, node) => sum + (node.depth || 0), 0) / graphNodes.length;
  return Math.round(averageDepth);
}

function transactionGroupsOverlap(a = {}, b = {}) {
  const minX = CASE_FLOW_VISUALS_TX_NODE_SIZE.width + 90;
  const minY = CASE_FLOW_VISUALS_TX_NODE_SIZE.height + 82;
  return Math.abs(a.x - b.x) < minX && Math.abs(a.y - b.y) < minY;
}

function applyTransactionGroupSpacing(groups = [], options = {}) {
  if (!options.mergeTransactionNodes || !groups || groups.length <= 1) return;
  const minVerticalGap = Math.max(CASE_FLOW_VISUALS_TX_NODE_SIZE.height + 92, CASE_FLOW_VISUALS_GRAPH_SETTINGS.verticalSpacing * 0.82);
  const byDepth = new Map();
  groups.forEach((group) => {
    const key = group.visualDepth;
    if (!byDepth.has(key)) byDepth.set(key, []);
    byDepth.get(key).push(group);
  });
  byDepth.forEach((list) => {
    if (list.length <= 1) return;
    list.sort((a, b) => a.y - b.y || String(a.pathLabel).localeCompare(String(b.pathLabel), undefined, { numeric: true }));
    const originalCenter = list.reduce((sum, group) => sum + group.y, 0) / list.length;
    for (let index = 1; index < list.length; index += 1) {
      const previous = list[index - 1];
      const current = list[index];
      const needed = previous.y + minVerticalGap;
      if (current.y < needed) current.y = needed;
    }
    const newCenter = list.reduce((sum, group) => sum + group.y, 0) / list.length;
    const correction = newCenter - originalCenter;
    list.forEach((group) => { group.y -= correction; });
  });
  for (let pass = 0; pass < 8; pass += 1) {
    let changed = false;
    for (let i = 0; i < groups.length; i += 1) {
      for (let j = i + 1; j < groups.length; j += 1) {
        const a = groups[i];
        const b = groups[j];
        if (!transactionGroupsOverlap(a, b)) continue;
        const direction = a.y <= b.y ? 1 : -1;
        const push = minVerticalGap / 2;
        a.y -= direction * push;
        b.y += direction * push;
        changed = true;
      }
    }
    if (!changed) break;
  }
}

function txVisualId(graphId = '') {
  return `tx:${simpleHash(graphId)}`;
}

function prepareTransactionVisualGroups(graphNodes = [], positions = {}, layoutDirections = {}, options = {}) {
  const grouped = new Map();
  graphNodes.forEach((graphNode) => {
    const key = transactionVisualGroupKey(graphNode, options);
    if (!grouped.has(key)) grouped.set(key, { key, graphNodes: [] });
    grouped.get(key).graphNodes.push(graphNode);
  });

  const groups = Array.from(grouped.values()).map((group) => {
    const graphNodePositions = group.graphNodes.map(graphNode => positions[graphNode.id] || { x: 0, y: 0 });
    const averageX = graphNodePositions.reduce((sum, pos) => sum + pos.x, 0) / Math.max(1, graphNodePositions.length);
    const averageY = graphNodePositions.reduce((sum, pos) => sum + pos.y, 0) / Math.max(1, graphNodePositions.length);
    const primaryGraphNode = pickPrimaryGraphNodeForTransactionGroup(group.graphNodes);
    const primaryTx = primaryGraphNode.tx;
    const transactionHash = primaryTx.transactionHash || firstNonEmpty(group.graphNodes.map(node => node.tx.transactionHash));
    const normalizedHash = normalizeHash(transactionHash);
    const mergedByHash = options.mergeTransactionNodes && normalizedHash && group.graphNodes.length > 1;
    const layoutDirection = computeTransactionGroupLayoutDirection(group.graphNodes, layoutDirections);
    const visualId = mergedByHash ? `tx-merged:${simpleHash(normalizedHash)}` : txVisualId(primaryGraphNode.id);
    const shortHash = transactionHash ? `${caseFlowVisualsShortenMiddle(transactionHash, 8, 6)}${mergedByHash ? ` ×${group.graphNodes.length}` : ''}` : 'No hash';
    return {
      ...group,
      visualId,
      primaryGraphId: primaryGraphNode.id,
      primaryTx,
      transactions: group.graphNodes.map(node => node.tx),
      transactionHash,
      normalizedHash,
      shortHash,
      pathLabel: combinePathLabelsForMergedTransaction(group.graphNodes),
      x: averageX,
      y: averageY,
      originalX: averageX,
      originalY: averageY,
      visualDepth: computeTransactionGroupVisualDepth(group.graphNodes, averageX),
      layoutDirection,
      outputDirection: layoutDirection,
      inputDirection: -layoutDirection,
      mergedByHash
    };
  });
  applyTransactionGroupSpacing(groups, options);
  return groups;
}

function buildTransactionGroupLookup(groups = []) {
  const lookup = {};
  groups.forEach((group) => {
    group.graphNodes.forEach((graphNode) => {
      lookup[graphNode.id] = group;
    });
  });
  return lookup;
}

function uniqueNormalizedTexts(values = []) {
  const seen = new Set();
  const unique = [];
  (values || []).forEach((value) => {
    const text = normalizeCell(value);
    if (!text) return;
    const key = text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(text);
  });
  return unique;
}

function parseAmountTokenText(value = '', fallbackToken = '') {
  const text = normalizeCell(value);
  const tokenFromFallback = normalizeCell(fallbackToken);
  if (!text) return { amountText: '', token: tokenFromFallback };
  const amountMatch = text.match(/[-+]?(?:(?:\d[\d,]*)(?:\.\d+)?|\.\d+)(?:e[-+]?\d+)?/i);
  if (!amountMatch) return { amountText: '', token: tokenFromFallback };
  const amountText = amountMatch[0];
  const afterAmount = text.slice(amountMatch.index + amountText.length).trim();
  const tokenMatch = afterAmount.match(/^([A-Za-z][A-Za-z0-9._-]*)/);
  const token = tokenMatch ? tokenMatch[1].trim() : tokenFromFallback;
  return { amountText, token };
}

function normalizeTokenForAmountFormatting(token = '') {
  const raw = normalizeCell(token).toUpperCase().replace(/^\$+/, '');
  if (!raw) return '';
  const firstChunk = raw.split(/[\s/()[\],;:-]+/)[0] || raw;
  return firstChunk.replace(/\.E$/i, '').replace(/[^A-Z0-9]/g, '');
}

function isStableTokenForAmountFormatting(token = '') {
  return new Set(['USDT', 'DAI', 'USDC', 'USDD']).has(normalizeTokenForAmountFormatting(token));
}

function expandScientificDecimalText(value = '') {
  const text = normalizeCell(value);
  const match = text.match(/^(\d+)(?:\.(\d+))?[eE]([-+]?\d+)$/);
  if (!match) return text;
  const integer = match[1] || '0';
  const fraction = match[2] || '';
  const exponent = Number.parseInt(match[3] || '0', 10);
  const digits = `${integer}${fraction}`.replace(/^0+(?=\d)/, '') || '0';
  const decimalIndex = integer.length + exponent;
  if (decimalIndex <= 0) return `0.${'0'.repeat(Math.abs(decimalIndex))}${digits}`;
  if (decimalIndex >= digits.length) return `${digits}${'0'.repeat(decimalIndex - digits.length)}`;
  return `${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`;
}

function stripLeadingZeros(value = '') {
  const stripped = String(value || '').replace(/^0+(?=\d)/, '');
  return stripped || '0';
}

function parseDecimalParts(value = '') {
  let text = normalizeCell(value).replace(/,/g, '');
  if (!text) return null;
  let sign = '';
  if (text[0] === '-' || text[0] === '+') {
    sign = text[0] === '-' ? '-' : '';
    text = text.slice(1);
  }
  if (text.startsWith('.')) text = `0${text}`;
  text = expandScientificDecimalText(text);
  const match = text.match(/^(\d+)(?:\.(\d+))?$/);
  if (!match) return null;
  const integer = stripLeadingZeros(match[1] || '0') || '0';
  const fraction = match[2] || '';
  return { sign, integer, fraction };
}

function decimalPartsAreNonZero(parts = null) {
  if (!parts) return false;
  return /[1-9]/.test(`${parts.integer || ''}${parts.fraction || ''}`);
}

function decimalPartsWouldTruncateToZero(parts = null, decimals = 4) {
  if (!parts) return false;
  const integerIsZero = stripLeadingZeros(parts.integer || '0') === '0';
  const paddedFraction = String(parts.fraction || '').padEnd(decimals, '0');
  const keptFraction = paddedFraction.slice(0, decimals);
  return integerIsZero && !/[1-9]/.test(keptFraction);
}

function addThousandsSeparators(integer = '0') {
  return String(integer || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function joinDecimalParts(sign = '', integer = '0', fraction = '', decimals = 0) {
  const normalizedInteger = addThousandsSeparators(stripLeadingZeros(integer || '0'));
  const decimalText = decimals > 0 ? `.${fraction}` : '';
  const signText = sign === '-' && decimalPartsAreNonZero({ integer, fraction }) ? '-' : '';
  return `${signText}${normalizedInteger}${decimalText}`;
}

function formatDecimalPartsFixedTruncated(parts = null, decimals = 4) {
  if (!parts) return '';
  const fraction = String(parts.fraction || '').slice(0, decimals).padEnd(decimals, '0');
  return joinDecimalParts(parts.sign, parts.integer, fraction, decimals);
}

function addOneToIntegerString(integer = '0') {
  const digits = String(integer || '0').split('');
  let carry = 1;
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    const next = Number.parseInt(digits[index] || '0', 10) + carry;
    if (next >= 10) digits[index] = '0';
    else {
      digits[index] = String(next);
      carry = 0;
      break;
    }
  }
  if (carry) digits.unshift('1');
  return digits.join('');
}

function formatDecimalPartsRounded(parts = null, decimals = 8) {
  if (!parts) return '';
  const paddedFraction = String(parts.fraction || '').padEnd(decimals + 1, '0');
  const roundDigit = Number.parseInt(paddedFraction.charAt(decimals) || '0', 10);
  const kept = paddedFraction.slice(0, decimals).split('');
  let integer = parts.integer || '0';
  if (roundDigit >= 5) {
    let carry = 1;
    for (let index = kept.length - 1; index >= 0; index -= 1) {
      const next = Number.parseInt(kept[index] || '0', 10) + carry;
      if (next >= 10) kept[index] = '0';
      else {
        kept[index] = String(next);
        carry = 0;
        break;
      }
    }
    if (carry) integer = addOneToIntegerString(integer);
  }
  const fraction = kept.join('').padEnd(decimals, '0');
  return joinDecimalParts(parts.sign, integer, fraction, decimals);
}

function formatAmountByTokenRule(amountText = '', token = '') {
  const parsedInput = parseAmountTokenText(amountText, token);
  const parts = parseDecimalParts(parsedInput.amountText || amountText);
  if (!parts) return '';
  const displayToken = normalizeCell(parsedInput.token || token);
  const isStableToken = isStableTokenForAmountFormatting(displayToken);
  const defaultDecimals = isStableToken ? 2 : 4;
  let formattedAmount = formatDecimalPartsFixedTruncated(parts, defaultDecimals);
  if (!isStableToken && decimalPartsAreNonZero(parts) && decimalPartsWouldTruncateToZero(parts, 4)) {
    formattedAmount = formatDecimalPartsRounded(parts, 8);
  }
  return [formattedAmount, displayToken].filter(Boolean).join(' ').trim();
}

function formatNodeAmountText({ primaryText = '', fallbackAmount = '', fallbackToken = '' } = {}) {
  const primary = parseAmountTokenText(primaryText, fallbackToken);
  if (primary.amountText) {
    const formatted = formatAmountByTokenRule(primary.amountText, primary.token || fallbackToken);
    if (formatted) return formatted;
  }
  const fallback = parseAmountTokenText(fallbackAmount, fallbackToken);
  if (fallback.amountText) {
    const formatted = formatAmountByTokenRule(fallback.amountText, fallback.token || fallbackToken);
    if (formatted) return formatted;
  }
  const rawPrimary = normalizeCell(primaryText);
  if (rawPrimary) return rawPrimary;
  return [fallbackAmount, fallbackToken].filter(Boolean).join(' ').trim();
}

function getTransactionAmountText(tx = {}) {
  const formatted = formatNodeAmountText({ primaryText: tx.fundsFollowed, fallbackAmount: tx.amount, fallbackToken: tx.tokenType });
  return truncateText(formatted, 28) || 'Amount not shown';
}

function getTransactionGroupAmountText(group = {}) {
  const candidates = (group.transactions || [])
    .map(tx => getTransactionAmountText(tx))
    .filter(text => text && text !== 'Amount not shown');
  const unique = uniqueNormalizedTexts(candidates);
  if (unique.length === 0) return 'Amount not shown';
  if (unique.length === 1) return unique[0];
  return truncateText(unique.join(' / '), 28);
}

function getTransactionGroupChainText(group = {}) {
  const candidates = (group.transactions || [])
    .map(tx => tx.chainName || tx.tokenType || '')
    .filter(Boolean);
  const unique = uniqueNormalizedTexts(candidates);
  if (unique.length <= 1) return unique[0] || '';
  return truncateText(unique.join(' / '), 24);
}

function getTransactionOriginalAmountText(tx = {}) {
  const formatted = formatNodeAmountText({ primaryText: '', fallbackAmount: tx.amount, fallbackToken: tx.tokenType });
  return formatted && formatted !== 'Amount not shown' ? formatted : '';
}

function getTransactionGroupOriginalAmountText(group = {}) {
  const candidates = (group.transactions || [])
    .map(tx => getTransactionOriginalAmountText(tx))
    .filter(Boolean);
  const unique = uniqueNormalizedTexts(candidates);
  if (unique.length === 0) return '';
  if (unique.length === 1) return unique[0];
  return truncateText(unique.join(' / '), 36);
}

function normalizeFundsBarCellText(value = '') {
  let text = String(value ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\\r\\n|\\n|\\r/g, '\n')
    .replace(/\u00a0/g, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim();

  if (
    text.length >= 2 &&
    (
      (text.startsWith('"') && text.endsWith('"')) ||
      (text.startsWith("'") && text.endsWith("'"))
    )
  ) {
    text = text.slice(1, -1).replace(/""/g, '"').trim();
  }

  return text.replace(/^[`'"]+|[`'"]+$/g, '').trim();
}

function getFirstLineText(value = '') {
  return normalizeCell(
    (normalizeFundsBarCellText(value).split(/\n/)[0] || '')
      .replace(/^[`'"]+|[`'"]+$/g, '')
  );
}

function parseVisualAmountForBar(value = '', fallbackToken = '') {
  const source = normalizeCell(normalizeFundsBarCellText(value));
  const parsed = parseAmountTokenText(source, fallbackToken);
  const amount = parseNumber(parsed.amountText);

  return {
    amount,
    token: normalizeCell(parsed.token || fallbackToken),
    rawText: source,
    amountText: parsed.amountText || ''
  };
}

function parseLeftBoundsAmountForBar(value = '', fallbackToken = '') {
  const source = normalizeCell(normalizeFundsBarCellText(value));
  const firstLine = getFirstLineText(source);
  const amountMatch = firstLine.match(/[-+]?(?:(?:\d[\d,]*)(?:\.\d+)?|\.\d+)(?:e[-+]?\d+)?/i) ||
    source.match(/[-+]?(?:(?:\d[\d,]*)(?:\.\d+)?|\.\d+)(?:e[-+]?\d+)?/i);
  const amountText = amountMatch ? amountMatch[0] : '';
  const parsed = parseAmountTokenText(amountText, fallbackToken);
  return {
    amount: parseNumber(parsed.amountText || amountText),
    token: normalizeCell(fallbackToken),
    rawText: source,
    amountText: parsed.amountText || amountText
  };
}

function countFundsBarFractionDigits(amountText = '') {
  const text = normalizeCell(amountText).replace(/,/g, '');
  if (!text || /e/i.test(text)) return 8;
  const decimalIndex = text.indexOf('.');
  return decimalIndex >= 0 ? Math.max(0, text.length - decimalIndex - 1) : 0;
}

function fundsBarValueTolerance(parsed = {}, token = '') {
  const digits = countFundsBarFractionDigits(parsed?.amountText || '');
  if (digits <= 0) return 0;
  const base = 0.5 * (10 ** -digits);
  const normalizedToken = normalizeTokenForAmountFormatting(token || parsed?.token || '');
  const cap = isStableTokenForAmountFormatting(normalizedToken) ? 0.010000001 : 0.000100001;
  return Math.min(cap, base + Number.EPSILON);
}

function fundsBarComparisonTolerance(parsedValues = [], token = '') {
  const tolerances = (Array.isArray(parsedValues) ? parsedValues : [])
    .map(parsed => fundsBarValueTolerance(parsed, token))
    .filter(value => Number.isFinite(value) && value > 0);
  return Math.max(1e-12, ...tolerances);
}

function amountBarTokensCompatible(leftToken = '', rightToken = '') {
  const left = normalizeTokenForAmountFormatting(leftToken);
  const right = normalizeTokenForAmountFormatting(rightToken);
  return !left || !right || left === right;
}

function formatFundsFollowedBarAmount(amount = 0, token = '') {
  const numeric = Number(amount);
  const normalizedToken = normalizeCell(token);
  if (!Number.isFinite(numeric)) return '';
  const decimals = isStableTokenForAmountFormatting(normalizedToken) ? 2 : 8;
  const minimumFractionDigits = isStableTokenForAmountFormatting(normalizedToken) ? 2 : 0;
  const text = numeric.toLocaleString('en-US', {
    minimumFractionDigits,
    maximumFractionDigits: decimals
  });
  return [text, normalizedToken].filter(Boolean).join(' ').trim();
}

function buildFundsFollowedBarForTransaction(tx = {}) {
  const fallbackToken = normalizeCell(tx.tokenType);
  const total = parseVisualAmountForBar(tx.amount, fallbackToken);
  const followed = parseVisualAmountForBar(tx.fundsFollowed, fallbackToken);
  const leftSource = normalizeCell(tx.leftBoundsNumeric) || getFirstLineText(tx.leftBounds);
  const left = parseLeftBoundsAmountForBar(leftSource || tx.leftBounds, fallbackToken);

  if (!Number.isFinite(total.amount) || !Number.isFinite(followed.amount) || !Number.isFinite(left.amount)) return null;
  if (total.amount <= 0 || followed.amount <= 0 || left.amount < 0) return null;
  if (!amountBarTokensCompatible(total.token, followed.token) || !amountBarTokensCompatible(total.token, left.token) || !amountBarTokensCompatible(followed.token, left.token)) return null;

  const token = normalizeCell(total.token || followed.token || left.token || fallbackToken);
  const endAmount = left.amount + followed.amount;
  const tolerance = Math.max(Math.abs(total.amount) * 1e-9, fundsBarComparisonTolerance([total, followed, left], token));
  if (left.amount > total.amount + tolerance || endAmount > total.amount + tolerance) return null;

  const clampedLeft = Math.max(0, Math.min(total.amount, left.amount));
  const clampedEnd = Math.max(clampedLeft, Math.min(total.amount, endAmount));
  const startRatio = clampedLeft / total.amount;
  const endRatio = clampedEnd / total.amount;
  const widthRatio = endRatio - startRatio;
  if (!Number.isFinite(startRatio) || !Number.isFinite(endRatio) || widthRatio <= 0) return null;

  const width = CASE_FLOW_VISUALS_TX_NODE_SIZE.width - 24;
  const height = 12;
  const x = -width / 2;
  const y = -CASE_FLOW_VISUALS_TX_NODE_SIZE.height / 2 - 22;
  const fillWidth = Math.max(1.25, widthRatio * width);
  const fillX = x + (startRatio * width);
  const totalText = formatFundsFollowedBarAmount(total.amount, token);
  const leftText = formatFundsFollowedBarAmount(clampedLeft, token);
  const followedText = formatFundsFollowedBarAmount(followed.amount, token);
  const endText = formatFundsFollowedBarAmount(clampedEnd, token);

  return {
    isValid: true,
    token,
    totalAmount: total.amount,
    leftAmount: clampedLeft,
    fundsFollowedAmount: followed.amount,
    endAmount: clampedEnd,
    startRatio,
    endRatio,
    widthRatio,
    x,
    y,
    width,
    height,
    fillX,
    fillWidth,
    rx: 5,
    totalText,
    leftText,
    followedText,
    endText,
    rangeText: `${leftText || '0'} → ${endText || ''}`.trim(),
    title: `Funds followed: ${followedText || normalizeCell(normalizeFundsBarCellText(tx.fundsFollowed))}. Left bounds: ${leftText || leftSource}. Transaction amount: ${totalText || normalizeCell(normalizeFundsBarCellText(tx.amount))}.`
  };
}

function buildFundsFollowedBarForTransactionGroup(group = {}) {
  const bars = (group.transactions || [])
    .map(tx => buildFundsFollowedBarForTransaction(tx))
    .filter(Boolean);
  if (bars.length === 0) return null;
  if (bars.length === 1) return bars[0];
  const first = bars[0];
  const same = bars.every(bar =>
    normalizeTokenForAmountFormatting(bar.token) === normalizeTokenForAmountFormatting(first.token) &&
    Math.abs(bar.totalAmount - first.totalAmount) <= Math.max(Math.abs(first.totalAmount) * 1e-9, 1e-12) &&
    Math.abs(bar.leftAmount - first.leftAmount) <= Math.max(Math.abs(first.totalAmount) * 1e-9, 1e-12) &&
    Math.abs(bar.fundsFollowedAmount - first.fundsFollowedAmount) <= Math.max(Math.abs(first.totalAmount) * 1e-9, 1e-12)
  );
  return same ? { ...first, title: `${first.title} Same funds-followed range appears across merged entries.` } : null;
}

function notesContainSwap(tx = {}) {
  const text = `${tx.note || ''} ${tx.userNotes || ''} ${tx.attribution || ''}`.toLowerCase();
  return /\b(swap|dex|router|amm|uniswap|sushiswap|curve|pancakeswap|trader joe|raydium|orca)\b/i.test(text);
}

function notesContainBridge(tx = {}) {
  const text = `${tx.note || ''} ${tx.userNotes || ''} ${tx.attribution || ''}`.toLowerCase();
  return /\b(bridge|bridged|bridging|debridge|stargate|hop protocol|across|synapse|wormhole|celer|layerzero)\b/i.test(text);
}

function getTransactionGroupBadge(group = {}, options = {}) {
  if (options.highlightSwaps && (group.transactions || []).some(tx => notesContainSwap(tx))) return 'SWAP';
  if (options.highlightBridges && (group.transactions || []).some(tx => notesContainBridge(tx))) return 'BRIDGE';
  return '';
}

function isBitcoinTxn(tx = {}) {
  const symbol = normalizeCell(tx.tokenType).toUpperCase();
  const chain = normalizeCell(tx.chainName).toLowerCase();
  return symbol === 'BTC' || symbol === 'XBT' || chain === 'bitcoin';
}

function getOutputsForVisual(tx = {}) {
  if (tx.outputs && tx.outputs.length > 0) return tx.outputs.filter(output => output.toAddress);
  return (tx.toAddresses || []).map(address => ({
    toAddress: address,
    amount: tx.amount,
    amountNumeric: tx.amountNumeric,
    tokenType: tx.tokenType,
    fundsFollowed: tx.fundsFollowed,
    leftBounds: tx.leftBounds,
    leftBoundsNumeric: tx.leftBoundsNumeric
  }));
}

function dedupeTransactionGroupOutputs(outputs = []) {
  const seen = new Set();
  const unique = [];
  (outputs || []).forEach((output) => {
    const sourceHash = normalizeHash(output.sourceTx && output.sourceTx.transactionHash) || output.sourceGraphId || '';
    const key = [
      sourceHash,
      output.sourceIndex,
      normalizeAddress(output.toAddress),
      normalizeTokenForAmountFormatting(output.tokenType || ''),
      String(output.amount || '').trim().replace(/,/g, ''),
      String(output.fundsFollowed || '').trim().replace(/\s+/g, ' ').toLowerCase(),
      String(output.leftBoundsNumeric || '').trim()
    ].join('|');
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(output);
  });
  return unique;
}

function getOutputsForTransactionGroup(group = {}) {
  const outputs = [];
  (group.graphNodes || []).forEach((graphNode) => {
    getOutputsForVisual(graphNode.tx).forEach((output, index) => {
      outputs.push({
        ...output,
        sourceTx: graphNode.tx,
        sourceGraphId: graphNode.id,
        sourceIndex: index,
        isBitcoinOutput: isBitcoinTxn(graphNode.tx)
      });
    });
  });
  return dedupeTransactionGroupOutputs(outputs);
}

function getUniqueFromAddressesForGraphNodes(graphNodes = []) {
  const froms = [];
  const seen = new Set();
  (graphNodes || []).forEach((graphNode) => {
    (graphNode.tx.fromAddresses || []).forEach((address) => {
      const key = normalizeAddress(address);
      if (!key || seen.has(key)) return;
      seen.add(key);
      froms.push(address);
    });
  });
  return froms;
}

function findLinkAddress(parentTx = {}, childTx = {}) {
  const parentOutputs = new Set((parentTx.toAddresses || []).map(addr => normalizeAddress(addr)));
  const childFroms = childTx.fromAddresses || [];
  return childFroms.find(addr => parentOutputs.has(normalizeAddress(addr))) || '';
}

function findBridgeFallbackAddress(parentTx = {}, childTx = {}) {
  if (!notesContainBridge(parentTx) && !notesContainBridge(childTx)) return '';
  const preferredOutput = (parentTx.outputs || []).find(output => output.toAddress && output.fundsFollowed);
  if (preferredOutput) return preferredOutput.toAddress;
  const anyOutput = (parentTx.outputs || []).find(output => output.toAddress);
  if (anyOutput) return anyOutput.toAddress;
  return (parentTx.toAddresses || []).find(Boolean) || '';
}

function parentAddressKey(parentId = '', address = '') {
  return `${parentId || ''}|${normalizeAddress(address)}`;
}

function getOutputAmountText(output = {}, tx = {}) {
  const formatted = formatNodeAmountText({ primaryText: output.fundsFollowed, fallbackAmount: output.amount || tx.amount, fallbackToken: output.tokenType || tx.tokenType });
  return truncateText(formatted, 22) || 'BTC';
}

function getTransactionGroupOutputDirection(group = {}) {
  const direction = Number(group && (group.outputDirection || group.layoutDirection));
  return direction < 0 ? -1 : 1;
}

function getTransactionGroupInputDirection(group = {}) {
  const direction = Number(group && group.inputDirection);
  if (direction < 0) return -1;
  if (direction > 0) return 1;
  return -getTransactionGroupOutputDirection(group);
}

function combineUniqueText(values = [], separator = ' / ') {
  return uniqueNormalizedTexts(values).join(separator);
}

function combineTransactionGroupRows(group = {}) {
  const rowNumbers = [];
  (group.transactions || []).forEach((tx) => {
    (tx.rowNumbers || [tx.startRow]).forEach((rowNumber) => {
      if (rowNumber && !rowNumbers.includes(rowNumber)) rowNumbers.push(rowNumber);
    });
  });
  return rowNumbers.sort((a, b) => a - b).join(', ');
}

function computePathLabelMetrics(label = '') {
  const lines = buildPathLabelLines(label);
  const longest = Math.max(1, ...lines.map(line => String(line || '').length));
  const boxWidth = Math.min(CASE_FLOW_VISUALS_TX_NODE_SIZE.width + 70, 300);
  const maxTextWidth = boxWidth - 24;
  const estimatedFont = Math.floor(maxTextWidth / Math.max(1, longest * 0.58));
  const fontSize = Math.max(18, Math.min(34, estimatedFont));
  const lineHeight = Math.ceil(fontSize * 1.1);
  const boxHeight = Math.max(44, (lines.length * lineHeight) + 16);
  const startY = -(((lines.length - 1) * lineHeight) / 2) + (fontSize * 0.34);
  return { lines, fontSize, lineHeight, startY, boxWidth, boxHeight };
}

function buildPathLabelLines(label = '') {
  const text = normalizeCell(label) || '(no label)';
  if (text.length <= 16) return [text];
  const maxLines = 2;
  const maxChars = text.length > 30 ? 16 : 18;
  const wordWrapped = wrapVisualLabel(text, maxChars, maxLines);
  if (wordWrapped.length > 1 || text.includes(' ')) return wordWrapped;
  const splitTokens = ['-', '/', '_', ':'];
  let bestSplit = -1;
  splitTokens.some((token) => {
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const matches = [...text.matchAll(new RegExp(escaped, 'g'))]
      .map(match => match.index)
      .filter(idx => idx > 3 && idx < text.length - 3);
    if (!matches.length) return false;
    bestSplit = matches.sort((a, b) => Math.abs(a - (text.length / 2)) - Math.abs(b - (text.length / 2)))[0] + 1;
    return true;
  });
  if (bestSplit > 0) {
    const first = text.slice(0, bestSplit).trim();
    let second = text.slice(bestSplit).trim();
    if (second.length > maxChars) second = `${second.slice(0, Math.max(1, maxChars - 1))}…`;
    return [first, second].filter(Boolean).slice(0, maxLines);
  }
  const first = text.slice(0, maxChars);
  let second = text.slice(maxChars, maxChars * 2);
  if (text.length > maxChars * 2) second = `${second.slice(0, Math.max(1, maxChars - 1))}…`;
  return [first, second].filter(Boolean).slice(0, maxLines);
}

function buildTransactionDetailRows(nodeOrGroup = {}, suppliedFundsBar = null) {
  const group = nodeOrGroup.graphNodes ? nodeOrGroup : null;
  if (group) {
    const first = (group.transactions || [])[0] || {};
    const fundsBar = suppliedFundsBar || null;
    return [
      ['Type', 'Transaction'],
      ['Path label', group.pathLabel || ''],
      ['Transaction hash', group.transactionHash || ''],
      ['Date', formatDateForNode(first.transactionDateRaw || '')],
      ['Amount shown', getTransactionGroupAmountText(group)],
      ['Transaction amount', getTransactionGroupOriginalAmountText(group)],
      ['Funds followed', fundsBar?.followedText || combineUniqueText((group.transactions || []).map(tx => tx.fundsFollowed), ' / ')],
      ['Left bounds', fundsBar?.leftText || combineUniqueText((group.transactions || []).map(tx => normalizeCell(tx.leftBoundsNumeric) || getFirstLineText(tx.leftBounds)), ' / ')],
      ['Followed range', fundsBar?.rangeText || ''],
      ['Chain', getTransactionGroupChainText(group)],
      ['Spreadsheet rows', combineTransactionGroupRows(group)],
      ['Merged entries', group.graphNodes?.length > 1 ? group.graphNodes.length : 'No'],
      ['Swap highlight', (group.transactions || []).some(tx => notesContainSwap(tx)) ? 'Yes' : 'No'],
      ['Bridge highlight', (group.transactions || []).some(tx => notesContainBridge(tx)) ? 'Yes' : 'No']
    ].filter(row => normalizeCell(row[1]));
  }
  return [];
}

function buildAddressDetailRows(address = '', labelRecord = null, sourceCount = 1) {
  return [
    ['Type', 'Address'],
    ['Address', address || ''],
    ['Legend label', labelRecord?.label || ''],
    ['Chain', labelRecord?.chainName || labelRecord?.chainId || ''],
    ['Occurrences in visual', sourceCount > 1 ? sourceCount : '1']
  ].filter(row => normalizeCell(row[1]));
}

function buildAmountDetailRows(node = {}) {
  return [
    ['Type', 'BTC amount'],
    ['Amount', node.amountText || ''],
    ['Output address', node.outputAddress || ''],
    ['Source hash', node.sourceTransactionHash || ''],
    ['Token', node.tokenType || '']
  ].filter(row => normalizeCell(row[1]));
}

function formatDateForNode(value = '') {
  if (!value) return '';
  const text = normalizeCell(value);
  if (text.length <= 22) return text;
  return text.slice(0, 22);
}

function getAddressLabelInfo(addressOrNormalized = '', addressLabelMap = new Map(), options = {}) {
  if (!options.labelAddresses) return { label: '', labelLines: [], category: 'regular', labelFontSize: 17 };
  const key = normalizeAddress(addressOrNormalized);
  const meta = addressLabelMap.get(key);
  if (!meta || (!meta.label && typeof meta !== 'string')) return { label: '', labelLines: [], category: 'regular', labelFontSize: 17 };
  const label = typeof meta === 'string' ? meta : meta.label;
  const category = typeof meta === 'string' ? classifyAddressLabel(meta) : (meta.category || 'regular');
  const cleanLabel = normalizeCell(label).replace(/\s+/g, ' ');
  const labelLines = Array.isArray(meta.labelLines) ? meta.labelLines : wrapVisualLabel(cleanLabel, 24, 3);
  return { label: cleanLabel, labelLines, category, labelFontSize: meta.labelFontSize || computeAddressLabelFontSize(labelLines) };
}

function makeVisualNodeBase(id, type, x, y, extra = {}) {
  return { id, type, x, y, ...extra };
}

function createFallbackLinkNodeForTransactionGroups(parentGroup, childGroup, address, upsertAddressNode, addEdge) {
  if (!parentGroup || !childGroup || !address) return null;
  const x = (parentGroup.x + childGroup.x) / 2;
  const y = (parentGroup.y + childGroup.y) / 2;
  const fallbackId = upsertAddressNode(address, x, y, {
    ownerId: `${parentGroup.visualId}|${childGroup.visualId}`,
    ownerTxGroupId: parentGroup.visualId,
    role: 'fallback-link'
  });
  if (fallbackId) addEdge(parentGroup.visualId, fallbackId, { kind: 'tx-to-fallback-address', dashed: true });
  return fallbackId;
}

function nodeExtents(node = {}) {
  if (node.type === 'address') {
    const labelLines = Array.isArray(node.labelLines) ? node.labelLines.length : 0;
    return {
      left: CASE_FLOW_VISUALS_ADDRESS_RADIUS + 4,
      right: CASE_FLOW_VISUALS_ADDRESS_RADIUS + 4,
      top: CASE_FLOW_VISUALS_ADDRESS_RADIUS + (labelLines ? 28 + labelLines * 20 : 4),
      bottom: CASE_FLOW_VISUALS_ADDRESS_RADIUS + 22
    };
  }
  if (node.type === 'amount') {
    return {
      left: CASE_FLOW_VISUALS_AMOUNT_NODE_SIZE.width / 2 + 3,
      right: CASE_FLOW_VISUALS_AMOUNT_NODE_SIZE.width / 2 + 3,
      top: CASE_FLOW_VISUALS_AMOUNT_NODE_SIZE.height / 2 + 3,
      bottom: CASE_FLOW_VISUALS_AMOUNT_NODE_SIZE.height / 2 + 3
    };
  }
  if (node.type === 'flowLabel') {
    return {
      left: (node.boxWidth || 420) / 2 + 4,
      right: (node.boxWidth || 420) / 2 + 4,
      top: (node.boxHeight || 56) / 2 + 4,
      bottom: (node.boxHeight || 56) / 2 + 4
    };
  }
  const fundsBar = node.fundsFollowedBar && typeof node.fundsFollowedBar === 'object' ? node.fundsFollowedBar : null;
  const fundsBarTop = fundsBar ? Math.abs(Math.min(0, Number(fundsBar.y) || 0)) + 4 : 0;
  return {
    left: CASE_FLOW_VISUALS_TX_NODE_SIZE.width / 2 + 4,
    right: CASE_FLOW_VISUALS_TX_NODE_SIZE.width / 2 + 4,
    top: Math.max(CASE_FLOW_VISUALS_TX_NODE_SIZE.height / 2 + 4, fundsBarTop),
    bottom: CASE_FLOW_VISUALS_TX_NODE_SIZE.height / 2 + 4
  };
}

function nodeCollisionRect(node = {}, padding = 0) {
  const extents = nodeExtents(node);
  const pad = Number(padding) || 0;
  const left = Math.max(4, extents.left + pad);
  const right = Math.max(4, extents.right + pad);
  const top = Math.max(4, extents.top + pad);
  const bottom = Math.max(4, extents.bottom + pad);
  return {
    minX: (Number(node.x) || 0) - left,
    maxX: (Number(node.x) || 0) + right,
    minY: (Number(node.y) || 0) - top,
    maxY: (Number(node.y) || 0) + bottom
  };
}

function rectsOverlap(a = {}, b = {}, minimumOverlap = 0) {
  const overlapX = Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX);
  const overlapY = Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY);
  const threshold = Math.max(0, Number(minimumOverlap) || 0);
  return overlapX > threshold && overlapY > threshold;
}

function clusterNodesByX(nodes = [], tolerance = 72) {
  const sorted = [...nodes].sort((a, b) => a.x - b.x || a.y - b.y);
  const columns = [];
  sorted.forEach((node) => {
    let column = columns.find(candidate => Math.abs(candidate.centerX - node.x) <= tolerance);
    if (!column) {
      column = { centerX: node.x, nodes: [] };
      columns.push(column);
    }
    column.nodes.push(node);
    column.centerX = column.nodes.reduce((sum, item) => sum + item.x, 0) / column.nodes.length;
  });
  return columns.map(column => column.nodes);
}

function bumpNodesAroundY(nodes = [], centerY = 0, bump = 0) {
  const half = bump / 2;
  nodes.forEach((node) => {
    if (node.y < centerY) node.y -= half;
    else if (node.y > centerY) node.y += half;
  });
}

function spaceCrowdedAddressColumns(nodes = []) {
  const addressNodes = nodes.filter(node => node.type === 'address');
  if (addressNodes.length < 2) return 0;
  const touched = new Set();
  const columnTolerance = Math.max(CASE_FLOW_VISUALS_ADDRESS_RADIUS * 1.6, 72);
  const maxIterations = 16;
  for (let iteration = 0; iteration < maxIterations; iteration += 1) {
    const columns = clusterNodesByX(addressNodes, columnTolerance);
    let changed = false;
    for (const column of columns) {
      column.sort((a, b) => a.y - b.y);
      for (let i = 0; i < column.length - 1; i += 1) {
        const upper = column[i];
        const lower = column[i + 1];
        const upperExtents = nodeExtents(upper);
        const lowerExtents = nodeExtents(lower);
        const requiredGap = Math.max(CASE_FLOW_VISUALS_ADDRESS_RADIUS * 2 + 30, upperExtents.bottom + lowerExtents.top + 16);
        const currentGap = lower.y - upper.y;
        if (currentGap >= requiredGap) continue;
        const bump = (requiredGap - currentGap) + 8;
        const centerY = (upper.y + lower.y) / 2;
        bumpNodesAroundY(nodes, centerY, bump);
        touched.add(upper.id);
        touched.add(lower.id);
        changed = true;
      }
    }
    if (!changed) break;
  }
  return touched.size;
}

function collectAddressColumnClusters(group = []) {
  const sorted = [...group].sort((a, b) => a.x - b.x || a.y - b.y);
  const columnTolerance = Math.max(80, CASE_FLOW_VISUALS_GRAPH_SETTINGS.horizontalSpacing * 0.45);
  const clusters = [];
  let index = 0;
  while (index < sorted.length) {
    const cluster = [sorted[index]];
    const baseX = sorted[index].x;
    index += 1;
    while (index < sorted.length && Math.abs(sorted[index].x - baseX) <= columnTolerance) {
      cluster.push(sorted[index]);
      index += 1;
    }
    if (cluster.length >= 2) clusters.push(cluster);
  }
  return clusters;
}

function chooseMergedAddressSurvivor(cluster = [], mode = 'sameColumnMiddle') {
  if (!cluster || !cluster.length) return null;
  const ordered = [...cluster].sort((a, b) => a.y - b.y || a.x - b.x);
  if (mode === 'sameColumnTop') return ordered[0];
  if (mode === 'globalAverage') return ordered[0];
  return ordered[Math.floor(ordered.length / 2)];
}

function mergeAddressNodeIntoSurvivor(node = {}, survivor = {}, edges = []) {
  edges.forEach((edge) => {
    if (edge.from === node.id) edge.from = survivor.id;
    if (edge.to === node.id) edge.to = survivor.id;
  });
  survivor.sourceCount = (survivor.sourceCount || 1) + (node.sourceCount || 1);
  if ((!survivor.label || !survivor.labelLines || survivor.labelLines.length === 0) && node.label) {
    survivor.label = node.label;
    survivor.labelLines = node.labelLines || [];
    survivor.labelCategory = node.labelCategory || 'regular';
    survivor.labelFontSize = node.labelFontSize || survivor.labelFontSize;
  }
  const mergedRoles = new Set();
  if (survivor.sourceRoles && survivor.sourceRoles.forEach) survivor.sourceRoles.forEach(role => mergedRoles.add(role));
  if (node.sourceRoles && node.sourceRoles.forEach) node.sourceRoles.forEach(role => mergedRoles.add(role));
  survivor.sourceRoles = mergedRoles;
}

function rebuildVisualLookup(nodes = [], lookup = {}) {
  Object.keys(lookup).forEach(key => delete lookup[key]);
  nodes.forEach((node) => { lookup[node.id] = node; });
}

function cleanVisualEdges(edges = [], lookup = {}) {
  const seen = new Set();
  for (let i = edges.length - 1; i >= 0; i -= 1) {
    const edge = edges[i];
    if (!edge || !lookup[edge.from] || !lookup[edge.to] || edge.from === edge.to) {
      edges.splice(i, 1);
      continue;
    }
    const key = `${edge.from}->${edge.to}:${edge.kind || ''}:${edge.dashed ? 'd' : 's'}`;
    if (seen.has(key)) {
      edges.splice(i, 1);
      continue;
    }
    seen.add(key);
    edge.id = `edge:${simpleHash(key)}`;
  }
}

function mergeAddressNodesByMode(nodes = [], edges = [], lookup = {}, mode = 'sameColumnMiddle') {
  const addressGroups = new Map();
  nodes.filter(node => node.type === 'address' && node.normalizedAddress).forEach((node) => {
    if (!addressGroups.has(node.normalizedAddress)) addressGroups.set(node.normalizedAddress, []);
    addressGroups.get(node.normalizedAddress).push(node);
  });
  const removedIds = new Set();
  let mergedCount = 0;
  addressGroups.forEach((group) => {
    if (group.length < 2) return;
    const clusters = mode === 'globalAverage' ? [group] : collectAddressColumnClusters(group);
    clusters.forEach((cluster) => {
      const activeCluster = cluster.filter(node => !removedIds.has(node.id));
      if (activeCluster.length < 2) return;
      const survivor = chooseMergedAddressSurvivor(activeCluster, mode);
      if (!survivor) return;
      if (mode === 'globalAverage') {
        survivor.x = activeCluster.reduce((sum, node) => sum + node.x, 0) / activeCluster.length;
        survivor.y = activeCluster.reduce((sum, node) => sum + node.y, 0) / activeCluster.length;
      }
      activeCluster.forEach((node) => {
        if (node.id === survivor.id) return;
        mergeAddressNodeIntoSurvivor(node, survivor, edges);
        removedIds.add(node.id);
        mergedCount += 1;
      });
    });
  });
  if (removedIds.size) {
    for (let i = nodes.length - 1; i >= 0; i -= 1) {
      if (removedIds.has(nodes[i].id)) nodes.splice(i, 1);
    }
    removedIds.forEach((id) => { delete lookup[id]; });
  }
  return mergedCount;
}

function applyAddressMergeAndSpacing(nodes = [], edges = [], lookup = {}, options = {}) {
  const stats = { mergedNodes: 0, spacedNodes: 0 };
  if (!Array.isArray(nodes) || !Array.isArray(edges)) return stats;
  if (options.mergeAddressMode !== 'none') stats.mergedNodes = mergeAddressNodesByMode(nodes, edges, lookup, options.mergeAddressMode);
  stats.spacedNodes = spaceCrowdedAddressColumns(nodes);
  rebuildVisualLookup(nodes, lookup);
  cleanVisualEdges(edges, lookup);
  return stats;
}

function relaxNonTransactionNodesAroundMergedTransactions(nodes = [], txGroups = []) {
  const mergedGroups = (txGroups || []).filter(group => group.mergedByHash);
  if (!mergedGroups.length || !nodes.length) return;
  mergedGroups.forEach((group) => {
    const nearby = nodes.filter(node => node.type !== 'tx' && node.ownerTxGroupId === group.visualId).sort((a, b) => a.y - b.y);
    if (nearby.length <= 1) return;
    const minGap = Math.max(CASE_FLOW_VISUALS_ADDRESS_RADIUS * 2 + 28, CASE_FLOW_VISUALS_AMOUNT_NODE_SIZE.height + 28);
    const center = nearby.reduce((sum, node) => sum + node.y, 0) / nearby.length;
    for (let index = 1; index < nearby.length; index += 1) {
      const previous = nearby[index - 1];
      const current = nearby[index];
      const needed = previous.y + minGap;
      if (current.y < needed) current.y = needed;
    }
    const newCenter = nearby.reduce((sum, node) => sum + node.y, 0) / nearby.length;
    const correction = newCenter - center;
    nearby.forEach((node) => { node.y -= correction; });
  });
}

function findVisualNodeOverlapPairs(nodes = [], padding = -4) {
  const items = (nodes || [])
    .filter(node => ['tx', 'address', 'amount', 'flowLabel'].includes(node.type))
    .map(node => ({ node, rect: nodeCollisionRect(node, padding) }))
    .filter(item => item.rect)
    .sort((a, b) => a.rect.minX - b.rect.minX || a.rect.minY - b.rect.minY);
  const pairs = [];
  for (let i = 0; i < items.length; i += 1) {
    const a = items[i];
    for (let j = i + 1; j < items.length; j += 1) {
      const b = items[j];
      if (b.rect.minX > a.rect.maxX) break;
      if (rectsOverlap(a.rect, b.rect, 1.5)) pairs.push([a.node, b.node]);
    }
  }
  return pairs;
}

function separateVisualOverlapPair(a = {}, b = {}, padding = 18) {
  if (!a || !b || a.id === b.id) return false;
  const rectA = nodeCollisionRect(a, 0);
  const rectB = nodeCollisionRect(b, 0);
  if (!rectA || !rectB) return false;
  const overlapX = Math.min(rectA.maxX, rectB.maxX) - Math.max(rectA.minX, rectB.minX);
  const overlapY = Math.min(rectA.maxY, rectB.maxY) - Math.max(rectA.minY, rectB.minY);
  if (overlapX <= 1.5 || overlapY <= 1.5) return false;
  const moveX = overlapX + padding;
  const moveY = overlapY + padding;
  const ax = Number(a.x) || 0;
  const bx = Number(b.x) || 0;
  const ay = Number(a.y) || 0;
  const by = Number(b.y) || 0;
  if (moveX < moveY) {
    let direction = ax <= bx ? -1 : 1;
    if (Math.abs(ax - bx) < 0.001) direction = String(a.id || '') <= String(b.id || '') ? -1 : 1;
    const delta = (moveX / 2) * direction;
    a.x = ax + delta;
    b.x = bx - delta;
    return true;
  }
  let direction = ay <= by ? -1 : 1;
  if (Math.abs(ay - by) < 0.001) direction = String(a.id || '') <= String(b.id || '') ? -1 : 1;
  const delta = (moveY / 2) * direction;
  a.y = ay + delta;
  b.y = by - delta;
  return true;
}

function cleanupVisualNodeOverlaps(nodes = [], layoutStats = {}) {
  const targets = (nodes || []).filter(node => ['tx', 'address', 'amount', 'flowLabel'].includes(node.type));
  const originalPositions = new Map(targets.map(node => [node.id, { x: node.x, y: node.y }]));
  const detectionPadding = -4;
  const separationPadding = 18;
  const maxIterations = 10;
  const initialOverlapCount = findVisualNodeOverlapPairs(targets, detectionPadding).length;
  if (!initialOverlapCount) {
    layoutStats.spacedAddressNodes = layoutStats.spacedAddressNodes || 0;
    return { initialOverlapCount: 0, remainingOverlapCount: 0, movedNodeCount: 0, iterations: 0 };
  }
  let iterations = 0;
  for (; iterations < maxIterations; iterations += 1) {
    const pairs = findVisualNodeOverlapPairs(targets, detectionPadding);
    if (!pairs.length) break;
    let movedThisRound = false;
    pairs.forEach(([a, b]) => {
      if (separateVisualOverlapPair(a, b, separationPadding)) movedThisRound = true;
    });
    if (!movedThisRound) break;
  }
  const remainingOverlapCount = findVisualNodeOverlapPairs(targets, detectionPadding).length;
  const movedNodeCount = targets.reduce((count, node) => {
    const original = originalPositions.get(node.id);
    if (!original) return count;
    const moved = Math.abs((Number(node.x) || 0) - original.x) > 0.1 || Math.abs((Number(node.y) || 0) - original.y) > 0.1;
    return count + (moved ? 1 : 0);
  }, 0);
  layoutStats.spacedAddressNodes = (layoutStats.spacedAddressNodes || 0) + movedNodeCount;
  return { initialOverlapCount, remainingOverlapCount, movedNodeCount, iterations };
}

function computeVisualBounds(nodes = []) {
  if (!Array.isArray(nodes) || nodes.length === 0) return { minX: 0, minY: 0, maxX: 1200, maxY: 700, width: 1200, height: 700 };
  const bounds = nodes.map(node => nodeCollisionRect(node, 0));
  const minX = Math.min(...bounds.map(bound => bound.minX));
  const minY = Math.min(...bounds.map(bound => bound.minY));
  const maxX = Math.max(...bounds.map(bound => bound.maxX));
  const maxY = Math.max(...bounds.map(bound => bound.maxY));
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

function createFlowLabelNode(flow = {}, bounds = {}) {
  const label = `${flow.mainLabel || 'Case flow'}${flow.mainHash ? ` · ${caseFlowVisualsShortenMiddle(flow.mainHash, 10, 8)}` : ''}`;
  return makeVisualNodeBase(`flow-label:${simpleHash(flow.key || label)}`, 'flowLabel', Math.max(bounds.minX + 260, 0), bounds.minY - 96, {
    labelText: label,
    boxWidth: Math.max(360, Math.min(720, label.length * 14)),
    boxHeight: 56,
    fontSize: label.length > 45 ? 20 : 24,
    detailRows: [['Type', 'Flow label'], ['Flow', flow.mainLabel || ''], ['Root hash', flow.mainHash || '']]
  });
}

function computeVisualLabelStats(nodes = [], addressLabelMap = new Map()) {
  const shown = new Set();
  nodes.filter(node => node.type === 'address' && node.normalizedAddress && node.label).forEach(node => shown.add(node.normalizedAddress));
  const parsed = addressLabelMap.size || 0;
  return { applied: shown.size, unmatched: Math.max(0, parsed - shown.size) };
}

export function buildCaseFlowVisual(flow = {}, state = {}, options = {}) {
  const visualOptions = normalizeFlowOptions(options);
  const txs = flow.transactions || flow.entries || [];
  const graph = buildTransactionGraph(txs, flow.mainTxn, visualOptions);
  const positions = computeTransactionPositions(graph.nodes, visualOptions);
  const layoutDirections = {};
  graph.nodes.forEach((node) => { layoutDirections[node.id] = 1; });
  const wrapStats = applyTrunkWrapIfEnabled(graph, positions, layoutDirections, visualOptions);
  const txGroups = prepareTransactionVisualGroups(graph.nodes, positions, layoutDirections, visualOptions);
  const txGroupByGraphId = buildTransactionGroupLookup(txGroups);
  const nodes = [];
  const edges = [];
  const lookup = {};
  const warnings = [...(graph.warnings || [])];
  const edgeKeys = new Set();
  const parentAddressOutputLookup = new Map();
  const addressLabelMap = state.addressLabelMap instanceof Map ? state.addressLabelMap : new Map();

  const addNode = (node) => {
    if (!node || !node.id || lookup[node.id]) return;
    nodes.push(node);
    lookup[node.id] = node;
  };

  const addEdge = (from, to, meta = {}) => {
    if (!from || !to || from === to || !lookup[from] || !lookup[to]) return;
    const key = `${from}->${to}:${meta.kind || ''}:${meta.dashed ? 'd' : 's'}`;
    if (edgeKeys.has(key)) return;
    edgeKeys.add(key);
    edges.push({
      id: `edge:${simpleHash(key)}`,
      from,
      to,
      kind: meta.kind || 'default',
      dashed: !!meta.dashed,
      isBridge: !!meta.isBridge,
      isSwap: !!meta.isSwap
    });
  };

  const upsertAddressNode = (address, desiredX, desiredY, addressOptions = {}) => {
    if (!address) return null;
    const normalized = normalizeAddress(address);
    const addressId = `addr:${simpleHash(`${addressOptions.ownerId || ''}|${normalized}|${addressOptions.index || 0}|${addressOptions.role || 'addr'}|${Math.round(desiredX)}|${Math.round(desiredY)}`)}`;
    if (!lookup[addressId]) {
      const labelInfo = getAddressLabelInfo(normalized, addressLabelMap, visualOptions);
      const node = makeVisualNodeBase(addressId, 'address', desiredX, desiredY, {
        address,
        fullAddress: address,
        normalizedAddress: normalized,
        normalized,
        shortAddress: caseFlowVisualsShortenMiddle(address, 6, 5),
        label: labelInfo.label,
        labelLines: labelInfo.labelLines,
        labelCategory: labelInfo.category,
        labelFontSize: labelInfo.labelFontSize,
        sourceCount: 1,
        sourceRoles: new Set([addressOptions.role || 'address']),
        ownerTxGroupId: addressOptions.ownerTxGroupId || addressOptions.ownerId || '',
        detailRows: buildAddressDetailRows(address, labelInfo.label ? labelInfo : null, 1)
      });
      addNode(node);
    }
    return addressId;
  };

  txGroups.forEach((group) => {
    const tx = group.primaryTx || {};
    const pathLabel = group.pathLabel || tx.pathLabel || '(no label)';
    const pathMetrics = computePathLabelMetrics(pathLabel);
    const isSwap = visualOptions.highlightSwaps && group.transactions.some(candidate => notesContainSwap(candidate));
    const isBridge = visualOptions.highlightBridges && group.transactions.some(candidate => notesContainBridge(candidate));
    const fundsFollowedBar = visualOptions.showFundsFollowedBars ? buildFundsFollowedBarForTransactionGroup(group) : null;
    const txNode = makeVisualNodeBase(group.visualId, 'tx', group.x, group.y, {
      graphId: group.primaryGraphId,
      groupId: group.key,
      pathLabel,
      pathLabelLines: pathMetrics.lines,
      pathFontSize: pathMetrics.fontSize,
      pathLineHeight: pathMetrics.lineHeight,
      pathTextStartY: pathMetrics.startY,
      pathBoxWidth: pathMetrics.boxWidth,
      pathBoxHeight: pathMetrics.boxHeight,
      transactionHash: group.transactionHash,
      hash: group.transactionHash,
      fullHash: group.transactionHash,
      shortHash: group.shortHash,
      dateText: formatDateForNode(tx.transactionDateRaw),
      amountText: getTransactionGroupAmountText(group),
      chainText: getTransactionGroupChainText(group),
      isSwap,
      isBridge,
      badge: getTransactionGroupBadge(group, visualOptions),
      fundsFollowedBar,
      transactionAmountText: getTransactionGroupOriginalAmountText(group),
      mergedSourceCount: group.graphNodes.length,
      sourceCount: group.graphNodes.length,
      mergedByHash: group.mergedByHash,
      sourceRows: combineTransactionGroupRows(group),
      fromHashText: combineUniqueText(group.transactions.map(candidate => candidate.fromHash), ' / '),
      continuesAsText: combineUniqueText(group.transactions.map(candidate => candidate.continuesAs), ' / '),
      attributionText: combineUniqueText(group.transactions.map(candidate => candidate.attribution), ' / '),
      notesText: combineUniqueText(group.transactions.map(candidate => candidate.note || candidate.userNotes), ' | '),
      detailRows: buildTransactionDetailRows(group, fundsFollowedBar)
    });
    addNode(txNode);
  });

  txGroups.forEach((group) => {
    const txNodeId = group.visualId;
    const outputs = getOutputsForTransactionGroup(group);
    const outputCenter = (outputs.length - 1) / 2;
    const outputDirection = getTransactionGroupOutputDirection(group);
    outputs.forEach((output, index) => {
      const outY = group.y + (index - outputCenter) * CASE_FLOW_VISUALS_GRAPH_SETTINGS.addressVerticalOffset;
      const addressX = group.x + outputDirection * CASE_FLOW_VISUALS_GRAPH_SETTINGS.addressHorizontalOffset;
      const addressId = upsertAddressNode(output.toAddress, addressX, outY, {
        ownerId: group.visualId,
        ownerTxGroupId: group.visualId,
        index,
        role: 'output'
      });
      if (!addressId) return;
      const addressNode = lookup[addressId];
      if (addressNode) addressNode.detailRows = buildAddressDetailRows(addressNode.address, addressNode.label ? addressNode : null, addressNode.sourceCount || 1);
      group.graphNodes.forEach((graphNode) => {
        parentAddressOutputLookup.set(parentAddressKey(graphNode.id, output.toAddress), addressId);
        parentAddressOutputLookup.set(parentAddressKey(graphNode.tx.transactionHash, output.toAddress), addressId);
      });
      parentAddressOutputLookup.set(parentAddressKey(group.visualId, output.toAddress), addressId);
      parentAddressOutputLookup.set(parentAddressKey(group.transactionHash, output.toAddress), addressId);
      if (output.isBitcoinOutput) {
        const amountId = `amount:${simpleHash(`${group.visualId}|${index}|${output.toAddress}|${output.amount || ''}|${output.fundsFollowed || ''}`)}`;
        addNode(makeVisualNodeBase(amountId, 'amount', group.x + outputDirection * CASE_FLOW_VISUALS_GRAPH_SETTINGS.addressHorizontalOffset * 0.65, outY, {
          amountText: getOutputAmountText(output, output.sourceTx || group.primaryTx),
          ownerTxGroupId: group.visualId,
          outputAddress: output.toAddress || '',
          tokenType: output.tokenType || (output.sourceTx && output.sourceTx.tokenType) || group.primaryTx.tokenType || '',
          sourceTransactionHash: group.transactionHash || ''
        }));
        const amountNode = lookup[amountId];
        if (amountNode) amountNode.detailRows = buildAmountDetailRows(amountNode);
        addEdge(txNodeId, amountId, { kind: 'tx-to-btc-amount' });
        addEdge(amountId, addressId, { kind: 'btc-amount-to-address' });
      } else {
        addEdge(txNodeId, addressId, { kind: 'tx-to-address' });
      }
    });
  });

  txGroups.forEach((group) => {
    const rootLikeGraphNodes = group.graphNodes.filter((graphNode) => {
      if (!graphNode.parents || graphNode.parents.length === 0) return true;
      return !graphNode.parents.some((parent) => {
        const parentGroup = txGroupByGraphId[parent.id];
        return parentGroup && parentGroup.visualId !== group.visualId;
      });
    });
    if (rootLikeGraphNodes.length === 0) return;
    const froms = getUniqueFromAddressesForGraphNodes(rootLikeGraphNodes);
    const center = (froms.length - 1) / 2;
    const inputDirection = getTransactionGroupInputDirection(group);
    froms.forEach((address, index) => {
      const y = group.y + (index - center) * CASE_FLOW_VISUALS_GRAPH_SETTINGS.addressVerticalOffset;
      const x = group.x + inputDirection * CASE_FLOW_VISUALS_GRAPH_SETTINGS.addressHorizontalOffset;
      const addressId = upsertAddressNode(address, x, y, {
        ownerId: group.visualId,
        ownerTxGroupId: group.visualId,
        index,
        role: 'root-input'
      });
      if (addressId) addEdge(addressId, group.visualId, { kind: 'root-address-to-tx' });
    });
  });

  graph.nodes.forEach((parent) => {
    parent.children.forEach((child) => {
      const parentGroup = txGroupByGraphId[parent.id];
      const childGroup = txGroupByGraphId[child.id];
      if (!parentGroup || !childGroup || parentGroup.visualId === childGroup.visualId) return;
      const linkAddress = findLinkAddress(parent.tx, child.tx);
      if (linkAddress) {
        const outputId = parentAddressOutputLookup.get(parentAddressKey(parent.id, linkAddress))
          || parentAddressOutputLookup.get(parentAddressKey(parent.tx.transactionHash, linkAddress))
          || parentAddressOutputLookup.get(parentAddressKey(parentGroup.visualId, linkAddress));
        if (outputId) addEdge(outputId, childGroup.visualId, { kind: 'address-to-child-tx' });
        else {
          const fallback = createFallbackLinkNodeForTransactionGroups(parentGroup, childGroup, linkAddress, upsertAddressNode, addEdge);
          if (fallback) addEdge(fallback, childGroup.visualId, { kind: 'fallback-address-to-child', dashed: true });
        }
        return;
      }
      const bridgeAddress = findBridgeFallbackAddress(parent.tx, child.tx);
      if (bridgeAddress) {
        const outputId = parentAddressOutputLookup.get(parentAddressKey(parent.id, bridgeAddress))
          || parentAddressOutputLookup.get(parentAddressKey(parent.tx.transactionHash, bridgeAddress))
          || parentAddressOutputLookup.get(parentAddressKey(parentGroup.visualId, bridgeAddress));
        if (outputId) addEdge(outputId, childGroup.visualId, { kind: 'bridge-address-to-child', dashed: true });
        else {
          const fallback = createFallbackLinkNodeForTransactionGroups(parentGroup, childGroup, bridgeAddress, upsertAddressNode, addEdge);
          if (fallback) addEdge(fallback, childGroup.visualId, { kind: 'bridge-fallback-address-to-child', dashed: true });
        }
        return;
      }
      addEdge(parentGroup.visualId, childGroup.visualId, { kind: 'direct-tx-link', dashed: true });
    });
  });

  if (visualOptions.mergeTransactionNodes) relaxNonTransactionNodesAroundMergedTransactions(nodes, txGroups);
  const postStats = applyAddressMergeAndSpacing(nodes, edges, lookup, visualOptions);
  const layoutStats = {
    wrappedTrunkLength: wrapStats.wrappedTrunkLength,
    columnMergedNodes: postStats.mergedNodes,
    spacedAddressNodes: postStats.spacedNodes
  };
  if (visualOptions.cleanupOverlaps) cleanupVisualNodeOverlaps(nodes, layoutStats);
  rebuildVisualLookup(nodes, lookup);
  cleanVisualEdges(edges, lookup);
  const boundsBeforeLabel = computeVisualBounds(nodes);
  const labelNode = createFlowLabelNode(flow, boundsBeforeLabel);
  nodes.unshift(labelNode);
  lookup[labelNode.id] = labelNode;
  const bounds = computeVisualBounds(nodes);
  const padding = 120;
  const viewBox = {
    x: Math.floor(bounds.minX - padding),
    y: Math.floor(bounds.minY - padding),
    width: Math.ceil(Math.max(bounds.width + padding * 2, 980)),
    height: Math.ceil(Math.max(bounds.height + padding * 2, 620))
  };
  const labelStats = computeVisualLabelStats(nodes, addressLabelMap);
  return {
    rendererVersion: CASE_FLOW_VISUALS_RENDERER_VERSION,
    flowKey: flow.key,
    mainLabel: flow.mainLabel,
    mainHash: flow.mainHash,
    nodes,
    edges,
    lookup,
    warnings,
    viewBox,
    bounds,
    layoutStats,
    labelStats,
    stats: {
      transactionCount: txGroups.length,
      rawTransactionEntryCount: graph.nodes.length,
      mergedTransactionCount: Math.max(0, graph.nodes.length - txGroups.length),
      addressCount: nodes.filter(node => node.type === 'address').length,
      edgeCount: edges.length
    },
    summary: `Visualized ${flow.mainLabel || 'case flow'} with ${txGroups.length} transaction node${txGroups.length === 1 ? '' : 's'}, ${nodes.filter(node => node.type === 'address').length} address node${nodes.filter(node => node.type === 'address').length === 1 ? '' : 's'}, and ${edges.length} arrow${edges.length === 1 ? '' : 's'}.`
  };
}

function cloneCaseFlowVisualForCombinedView(visual = {}, options = {}) {
  const idPrefix = normalizeCell(options.idPrefix);
  const dx = Number(options.dx) || 0;
  const dy = Number(options.dy) || 0;
  const idMap = new Map();
  const nodes = (Array.isArray(visual.nodes) ? visual.nodes : []).map((node) => {
    const nextId = `${idPrefix}${node.id}`;
    idMap.set(node.id, nextId);
    const clone = {
      ...node,
      id: nextId,
      x: (Number(node.x) || 0) + dx,
      y: (Number(node.y) || 0) + dy,
      sourceVisualNodeId: node.sourceVisualNodeId || node.id,
      sourceVisualFlowKey: visual.flowKey || '',
      sourceVisualMainLabel: visual.mainLabel || ''
    };
    if (Array.isArray(node.pathLabelLines)) clone.pathLabelLines = node.pathLabelLines.slice();
    if (Array.isArray(node.labelLines)) clone.labelLines = node.labelLines.slice();
    if (Array.isArray(node.detailRows)) clone.detailRows = node.detailRows.map(row => Array.isArray(row) ? row.slice() : row);
    if (node.sourceRoles instanceof Set) clone.sourceRoles = new Set(Array.from(node.sourceRoles));
    return clone;
  });
  const edges = (Array.isArray(visual.edges) ? visual.edges : []).map((edge) => ({
    ...edge,
    id: `${idPrefix}${edge.id}`,
    from: idMap.get(edge.from) || `${idPrefix}${edge.from}`,
    to: idMap.get(edge.to) || `${idPrefix}${edge.to}`
  }));
  return { nodes, edges, idMap };
}

function buildCaseCombinedVisualLookup(nodes = []) {
  return (Array.isArray(nodes) ? nodes : []).reduce((lookup, node) => {
    if (node?.id) lookup[node.id] = node;
    return lookup;
  }, {});
}

function buildCaseCombinedVisualViewBox(nodes = []) {
  const bounds = computeVisualBounds(nodes);
  const padding = 140;
  return {
    bounds,
    viewBox: {
      x: Math.floor(bounds.minX - padding),
      y: Math.floor(bounds.minY - padding),
      width: Math.ceil(Math.max(bounds.width + padding * 2, 980)),
      height: Math.ceil(Math.max(bounds.height + padding * 2, 620))
    }
  };
}

export function buildCaseCombinedFlowVisual(flows = [], state = {}, options = {}) {
  const visualOptions = normalizeFlowOptions(options.visualOptions || options);
  const sourceFlows = (Array.isArray(flows) ? flows : []).filter(flow => flow && (flow.transactions?.length || flow.entries?.length));
  const mode = normalizeCell(options.mode || options.combinedMode || 'allTogether') === 'stacked' ? 'stacked' : 'allTogether';
  if (sourceFlows.length === 0) {
    return {
      rendererVersion: CASE_FLOW_VISUALS_RENDERER_VERSION,
      flowKey: mode === 'stacked' ? 'all-stacked' : 'all-together',
      mainLabel: mode === 'stacked' ? 'All stacked' : 'All together',
      mainHash: '',
      nodes: [],
      edges: [],
      lookup: {},
      warnings: [],
      viewBox: { x: 0, y: 0, width: 1200, height: 700 },
      bounds: { minX: 0, minY: 0, maxX: 1200, maxY: 700, width: 1200, height: 700 },
      layoutStats: {},
      labelStats: { applied: 0, unmatched: 0 },
      stats: { transactionCount: 0, rawTransactionEntryCount: 0, mergedTransactionCount: 0, addressCount: 0, edgeCount: 0 },
      combinedMode: mode,
      summary: 'No qualifying case flows are available to visualize.'
    };
  }

  if (mode === 'allTogether') {
    const transactions = sourceFlows.flatMap(flow => Array.isArray(flow.transactions) ? flow.transactions : (flow.entries || []));
    const combinedFlow = {
      id: 'case-flow-all-together',
      key: 'all-together',
      mainLabel: `All qualifying flows`,
      mainHash: '',
      mainTxn: sourceFlows[0]?.mainTxn || transactions[0] || null,
      subTransactions: transactions.slice(1),
      transactions,
      entries: transactions,
      transactionCount: transactions.length
    };
    const visual = buildCaseFlowVisual(combinedFlow, state, visualOptions);
    return {
      ...visual,
      flowKey: 'all-together',
      mainLabel: 'All qualifying flows',
      combinedMode: 'allTogether',
      sourceFlowCount: sourceFlows.length,
      summary: `Visualized ${sourceFlows.length} qualifying flow${sourceFlows.length === 1 ? '' : 's'} together with ${visual.stats?.transactionCount || 0} transaction node${(visual.stats?.transactionCount || 0) === 1 ? '' : 's'}, ${visual.stats?.addressCount || 0} address node${(visual.stats?.addressCount || 0) === 1 ? '' : 's'}, and ${visual.stats?.edgeCount || 0} arrow${(visual.stats?.edgeCount || 0) === 1 ? '' : 's'}.`
    };
  }

  const combinedNodes = [];
  const combinedEdges = [];
  const warnings = [];
  const aggregateStats = {
    transactionCount: 0,
    rawTransactionEntryCount: 0,
    mergedTransactionCount: 0,
    addressCount: 0,
    edgeCount: 0,
    wrappedTrunkLength: 0,
    columnMergedNodes: 0,
    spacedAddressNodes: 0
  };
  let nextTopY = 0;
  const marginBetweenFlows = Math.max(CASE_FLOW_VISUALS_GRAPH_SETTINGS.verticalSpacing, CASE_FLOW_VISUALS_GRAPH_SETTINGS.addressVerticalOffset) * 1.85;
  sourceFlows.forEach((flow, index) => {
    const visual = buildCaseFlowVisual(flow, state, visualOptions);
    const bounds = visual.bounds || computeVisualBounds(visual.nodes);
    const offsetY = index === 0 ? -bounds.minY : nextTopY - bounds.minY;
    const idSeed = `${flow.key || flow.mainLabel || index}-${index}`;
    const cloned = cloneCaseFlowVisualForCombinedView(visual, {
      idPrefix: `stack-${index}-${simpleHash(idSeed)}:`,
      dx: 0,
      dy: offsetY
    });
    combinedNodes.push(...cloned.nodes);
    combinedEdges.push(...cloned.edges);
    warnings.push(...(visual.warnings || []));
    aggregateStats.transactionCount += Number(visual.stats?.transactionCount) || 0;
    aggregateStats.rawTransactionEntryCount += Number(visual.stats?.rawTransactionEntryCount) || 0;
    aggregateStats.mergedTransactionCount += Number(visual.stats?.mergedTransactionCount) || 0;
    aggregateStats.addressCount += Number(visual.stats?.addressCount) || 0;
    aggregateStats.edgeCount += Number(visual.stats?.edgeCount) || 0;
    aggregateStats.wrappedTrunkLength += Number(visual.layoutStats?.wrappedTrunkLength) || 0;
    aggregateStats.columnMergedNodes += Number(visual.layoutStats?.columnMergedNodes) || 0;
    aggregateStats.spacedAddressNodes += Number(visual.layoutStats?.spacedAddressNodes) || 0;
    nextTopY = bounds.maxY + offsetY + marginBetweenFlows;
  });
  const lookup = buildCaseCombinedVisualLookup(combinedNodes);
  const { bounds, viewBox } = buildCaseCombinedVisualViewBox(combinedNodes);
  const addressLabelMap = state.addressLabelMap instanceof Map ? state.addressLabelMap : new Map();
  const labelStats = computeVisualLabelStats(combinedNodes, addressLabelMap);
  return {
    rendererVersion: CASE_FLOW_VISUALS_RENDERER_VERSION,
    flowKey: 'all-stacked',
    mainLabel: 'All qualifying flows stacked',
    mainHash: '',
    nodes: combinedNodes,
    edges: combinedEdges,
    lookup,
    warnings: Array.from(new Set(warnings)),
    viewBox,
    bounds,
    layoutStats: aggregateStats,
    labelStats,
    stats: {
      transactionCount: aggregateStats.transactionCount,
      rawTransactionEntryCount: aggregateStats.rawTransactionEntryCount,
      mergedTransactionCount: aggregateStats.mergedTransactionCount,
      addressCount: aggregateStats.addressCount,
      edgeCount: combinedEdges.length
    },
    combinedMode: 'stacked',
    sourceFlowCount: sourceFlows.length,
    summary: `Visualized ${sourceFlows.length} qualifying flow${sourceFlows.length === 1 ? '' : 's'} stacked in one viewport with ${aggregateStats.transactionCount} transaction node${aggregateStats.transactionCount === 1 ? '' : 's'}, ${aggregateStats.addressCount} address node${aggregateStats.addressCount === 1 ? '' : 's'}, and ${combinedEdges.length} arrow${combinedEdges.length === 1 ? '' : 's'}.`
  };
}



function prefixAggregateNodeId(id = '', prefix = '') {
  return `${prefix}${String(id || '')}`;
}

function cloneVisualNodeForAggregate(node = {}, prefix = '', offset = {}) {
  const sourceId = String(node.id || 'node');
  return {
    ...node,
    sourceVisualNodeId: sourceId,
    aggregateSourcePrefix: prefix,
    id: prefixAggregateNodeId(sourceId, prefix),
    x: (Number(node.x) || 0) + (Number(offset.x) || 0),
    y: (Number(node.y) || 0) + (Number(offset.y) || 0),
    detailRows: [
      ...(Array.isArray(node.detailRows) ? node.detailRows : []),
      ['Visual source', offset.flowLabel || '']
    ].filter(row => normalizeCell(row?.[1]))
  };
}

function cloneVisualEdgeForAggregate(edge = {}, prefix = '') {
  const sourceId = String(edge.id || `${edge.from}->${edge.to}`);
  return {
    ...edge,
    sourceVisualEdgeId: sourceId,
    id: prefixAggregateNodeId(sourceId, `${prefix}edge:`),
    from: prefixAggregateNodeId(edge.from, prefix),
    to: prefixAggregateNodeId(edge.to, prefix)
  };
}

function buildAggregateLayoutOffsets(visuals = [], mode = 'allStacked') {
  const offsets = [];
  if (mode === 'allTogether') {
    let cursorX = 0;
    visuals.forEach((visual, index) => {
      const box = visual.viewBox || visual.bounds || { x: 0, y: 0, width: 1200, height: 700 };
      offsets.push({
        x: cursorX - (Number(box.x) || 0),
        y: -(Number(box.y) || 0),
        flowLabel: visual.mainLabel ? `Flow ${visual.mainLabel}` : `Flow ${index + 1}`
      });
      cursorX += Math.max(980, Number(box.width) || 1200) + 320;
    });
    return offsets;
  }

  let cursorY = 0;
  visuals.forEach((visual, index) => {
    const box = visual.viewBox || visual.bounds || { x: 0, y: 0, width: 1200, height: 700 };
    offsets.push({
      x: -(Number(box.x) || 0),
      y: cursorY - (Number(box.y) || 0),
      flowLabel: visual.mainLabel ? `Flow ${visual.mainLabel}` : `Flow ${index + 1}`
    });
    cursorY += Math.max(620, Number(box.height) || 700) + 260;
  });
  return offsets;
}

export function buildCaseFlowAggregateVisual(flows = [], state = {}, options = {}) {
  const sourceFlows = (Array.isArray(flows) ? flows : []).filter(Boolean);
  const mode = options.aggregateMode === 'allTogether' ? 'allTogether' : 'allStacked';
  const visualOptions = normalizeFlowOptions(options);
  const sourceVisuals = sourceFlows.map(flow => buildCaseFlowVisual(flow, state, visualOptions));
  const offsets = buildAggregateLayoutOffsets(sourceVisuals, mode);
  const nodes = [];
  const edges = [];
  const lookup = {};
  const warnings = [];
  const layoutStats = {
    aggregateMode: mode,
    sourceVisualCount: sourceVisuals.length,
    wrappedTrunkLength: 0,
    columnMergedNodes: 0,
    spacedAddressNodes: 0
  };

  sourceVisuals.forEach((visual, index) => {
    const prefix = `flow${index + 1}:`;
    const offset = offsets[index] || { x: 0, y: 0, flowLabel: visual.mainLabel || `Flow ${index + 1}` };
    (visual.warnings || []).forEach(warning => warnings.push(warning));
    layoutStats.wrappedTrunkLength += Number(visual.layoutStats?.wrappedTrunkLength) || 0;
    layoutStats.columnMergedNodes += Number(visual.layoutStats?.columnMergedNodes) || 0;
    layoutStats.spacedAddressNodes += Number(visual.layoutStats?.spacedAddressNodes) || 0;
    (visual.nodes || []).forEach((node) => {
      const cloned = cloneVisualNodeForAggregate(node, prefix, offset);
      nodes.push(cloned);
      lookup[cloned.id] = cloned;
    });
    (visual.edges || []).forEach(edge => edges.push(cloneVisualEdgeForAggregate(edge, prefix)));
  });

  const bounds = computeVisualBounds(nodes);
  const padding = 140;
  const viewBox = nodes.length
    ? {
        x: Math.floor(bounds.minX - padding),
        y: Math.floor(bounds.minY - padding),
        width: Math.ceil(Math.max(bounds.width + padding * 2, 980)),
        height: Math.ceil(Math.max(bounds.height + padding * 2, 620))
      }
    : { x: 0, y: 0, width: 1200, height: 700 };
  const txCount = sourceVisuals.reduce((sum, visual) => sum + (Number(visual.stats?.transactionCount) || 0), 0);
  const addressCount = nodes.filter(node => node.type === 'address').length;
  const labelStats = computeVisualLabelStats(nodes, state.addressLabelMap instanceof Map ? state.addressLabelMap : new Map());

  return {
    rendererVersion: CASE_FLOW_VISUALS_RENDERER_VERSION,
    flowKey: mode,
    mainLabel: mode === 'allTogether' ? 'All flows together' : 'All flows stacked',
    mainHash: '',
    aggregateMode: mode,
    sourceFlowCount: sourceVisuals.length,
    sourceVisuals,
    nodes,
    edges,
    lookup,
    warnings: Array.from(new Set(warnings)),
    viewBox,
    bounds,
    layoutStats,
    labelStats,
    stats: {
      transactionCount: txCount,
      rawTransactionEntryCount: sourceVisuals.reduce((sum, visual) => sum + (Number(visual.stats?.rawTransactionEntryCount) || 0), 0),
      mergedTransactionCount: sourceVisuals.reduce((sum, visual) => sum + (Number(visual.stats?.mergedTransactionCount) || 0), 0),
      addressCount,
      edgeCount: edges.length
    },
    summary: `Visualized ${sourceVisuals.length} qualifying flow${sourceVisuals.length === 1 ? '' : 's'} ${mode === 'allTogether' ? 'together' : 'stacked'} with ${txCount} transaction node${txCount === 1 ? '' : 's'}, ${addressCount} address node${addressCount === 1 ? '' : 's'}, and ${edges.length} arrow${edges.length === 1 ? '' : 's'}.`
  };
}


function escapeXml(value = '') {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function safeCssToken(value = '') {
  return String(value || 'regular').toLowerCase().replace(/[^a-z0-9_-]/g, '') || 'regular';
}

export function caseFlowVisualNodeClass(node = {}, selectedNodeId = '') {
  const classes = ['case-flow-visual-node'];
  if (node.type === 'tx') classes.push('case-flow-visual-node--tx');
  if (node.type === 'address') classes.push('case-flow-visual-node--address');
  if (node.type === 'amount') classes.push('case-flow-visual-node--amount');
  if (node.type === 'flowLabel') classes.push('case-flow-visual-node--flow-label');
  if (node.isSwap) classes.push('is-swap');
  if (node.isBridge) classes.push('is-bridge');
  if (selectedNodeId && node.id === selectedNodeId) classes.push('is-selected');
  return classes.join(' ');
}

export function caseFlowVisualEdgeClass(edge = {}) {
  const classes = ['case-flow-visual-edge'];
  if (edge.dashed) classes.push('is-dashed');
  if (edge.kind && String(edge.kind).includes('bridge')) classes.push('is-bridge-edge');
  if (edge.isBridge) classes.push('is-bridge-edge');
  if (edge.isSwap) classes.push('is-swap-edge');
  return classes.join(' ');
}

function getCaseFlowVisualNodeSize(node = {}) {
  if (node.type === 'amount') return CASE_FLOW_VISUALS_AMOUNT_NODE_SIZE;
  if (node.type === 'flowLabel') return { width: node.boxWidth || 420, height: node.boxHeight || 56 };
  return CASE_FLOW_VISUALS_TX_NODE_SIZE;
}

function clipCaseFlowVisualEdgePoint(from = {}, to = {}) {
  const dx = (Number(to.x) || 0) - (Number(from.x) || 0);
  const dy = (Number(to.y) || 0) - (Number(from.y) || 0);
  if (dx === 0 && dy === 0) return { x: Number(from.x) || 0, y: Number(from.y) || 0 };
  if (from.type === 'address') {
    const length = Math.sqrt((dx * dx) + (dy * dy)) || 1;
    return {
      x: (Number(from.x) || 0) + (dx / length) * CASE_FLOW_VISUALS_ADDRESS_RADIUS,
      y: (Number(from.y) || 0) + (dy / length) * CASE_FLOW_VISUALS_ADDRESS_RADIUS
    };
  }
  const size = getCaseFlowVisualNodeSize(from);
  const halfW = Math.max(1, (Number(size.width) || CASE_FLOW_VISUALS_TX_NODE_SIZE.width) / 2);
  const halfH = Math.max(1, (Number(size.height) || CASE_FLOW_VISUALS_TX_NODE_SIZE.height) / 2);
  const scale = 1 / Math.max(Math.abs(dx) / halfW, Math.abs(dy) / halfH, 1);
  return {
    x: (Number(from.x) || 0) + dx * scale,
    y: (Number(from.y) || 0) + dy * scale
  };
}

export function caseFlowVisualEdgePath(edge = {}, nodeLookup = new Map()) {
  const from = nodeLookup.get(edge.from);
  const to = nodeLookup.get(edge.to);
  if (!from || !to) return '';
  const start = clipCaseFlowVisualEdgePoint(from, to);
  const end = clipCaseFlowVisualEdgePoint(to, from);
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (Math.abs(dy) > CASE_FLOW_VISUALS_GRAPH_SETTINGS.verticalSpacing * 0.75 && Math.abs(dx) < CASE_FLOW_VISUALS_GRAPH_SETTINGS.addressHorizontalOffset * 0.8) {
    const midY = (start.y + end.y) / 2;
    return `M ${start.x} ${start.y} C ${start.x} ${midY}, ${end.x} ${midY}, ${end.x} ${end.y}`;
  }
  const curve = Math.min(170, Math.max(24, Math.abs(dx) * 0.45));
  const c1x = start.x + (dx >= 0 ? curve : -curve);
  const c2x = end.x - (dx >= 0 ? curve : -curve);
  return `M ${start.x} ${start.y} C ${c1x} ${start.y}, ${c2x} ${end.y}, ${end.x} ${end.y}`;
}

export function caseFlowVisualSvgStyles() {
  return `
    .case-flow-visual-bg{fill:#ffffff;}
    .case-flow-visual-edge{fill:none;stroke:#242424;stroke-width:3.4;stroke-linecap:round;opacity:.92;}
    .case-flow-visual-edge.is-dashed{stroke-dasharray:10 8;opacity:.62;}
    .case-flow-visual-edge.is-bridge-edge{stroke:#0d5aa7;stroke-width:4.6;}
    .case-flow-visual-edge.is-swap-edge{stroke:#7c4d00;}
    .case-flow-visual-arrow{fill:#242424;}
    .case-flow-visual-arrow-muted{fill:#555555;}
    .case-flow-visual-node text{font-family:Arial,Helvetica,sans-serif;}
    .case-flow-visual-tx-rect{fill:#f2c200;stroke:#111;stroke-width:3;}
    .case-investigation-visualizer-funds-bar__track{fill:#020617;opacity:.94;}
    .case-investigation-visualizer-funds-bar__fill{fill:#22c55e;opacity:.96;}
    .case-investigation-visualizer-funds-bar__outline{fill:none;stroke:rgba(255,255,255,.88);stroke-width:1.5;vector-effect:non-scaling-stroke;}
    .case-flow-visual-node--tx.is-swap .case-flow-visual-tx-rect{fill:#fee58a;stroke:#7c4d00;stroke-width:6;}
    .case-flow-visual-node--tx.is-bridge .case-flow-visual-tx-rect{fill:#c9e8ff;stroke:#0d5aa7;stroke-width:6;}
    .case-flow-visual-node.is-selected .case-flow-visual-tx-rect,.case-flow-visual-node.is-selected .case-flow-visual-address-circle,.case-flow-visual-node.is-selected .case-flow-visual-amount-rect,.case-flow-visual-node.is-selected .case-flow-visual-flow-label-bg{stroke:#00a8ff;stroke-width:7;}
    .case-flow-visual-address-circle{fill:#2f6fff;stroke:#111;stroke-width:3;}
    .case-flow-visual-amount-rect{fill:#ff9a00;stroke:#111;stroke-width:3;}
    .case-flow-visual-hash-text,.case-flow-visual-address-text{fill:#fff;stroke:#111;stroke-width:3;paint-order:stroke;font-weight:700;font-size:17px;}
    .case-flow-visual-amount-text{fill:#111;font-weight:700;font-size:14px;}
    .case-flow-visual-amount-node-text{fill:#111;font-weight:700;font-size:14px;}
    .case-flow-visual-date-text,.case-flow-visual-chain-text{fill:#111;font-size:12px;}
    .case-flow-visual-path-bg{fill:rgba(255,255,255,.74);stroke:rgba(120,0,0,.18);stroke-width:1;}
    .case-flow-visual-path-text{fill:#a4161a;font-weight:800;}
    .case-flow-visual-badge-text{fill:#111;font-weight:800;font-size:18px;}
    .case-flow-visual-address-label{font-weight:800;font-size:16px;}
    .case-flow-visual-address-label-regular{fill:#1d1d1d;}
    .case-flow-visual-address-label-exchange{fill:#0d5aa7;}
    .case-flow-visual-address-label-service{fill:#6d2aa8;}
    .case-flow-visual-address-count{fill:#111;font-weight:700;font-size:14px;}
    .case-flow-visual-flow-label-bg{fill:#fff7d6;stroke:#7a5c00;stroke-width:2.5;}
    .case-flow-visual-flow-label-text{fill:#3b2b00;font-weight:900;}
  `;
}

function renderSvgFundsFollowedBar(bar = null) {
  if (!bar || !bar.isValid) return '';
  return [
    `<g class="case-investigation-visualizer-funds-bar" aria-label="Funds followed bar">`,
    `<title>${escapeXml(bar.title || 'Funds followed')}</title>`,
    `<rect class="case-investigation-visualizer-funds-bar__track" x="${bar.x}" y="${bar.y}" width="${bar.width}" height="${bar.height}" rx="${bar.rx || 5}" ry="${bar.rx || 5}"/>`,
    `<rect class="case-investigation-visualizer-funds-bar__fill" x="${bar.fillX}" y="${bar.y}" width="${bar.fillWidth}" height="${bar.height}" rx="${bar.rx || 5}" ry="${bar.rx || 5}"/>`,
    `<rect class="case-investigation-visualizer-funds-bar__outline" x="${bar.x}" y="${bar.y}" width="${bar.width}" height="${bar.height}" rx="${bar.rx || 5}" ry="${bar.rx || 5}"/>`,
    `</g>`
  ].join('\n');
}

function renderSvgTxNode(node = {}) {
  const width = CASE_FLOW_VISUALS_TX_NODE_SIZE.width;
  const height = CASE_FLOW_VISUALS_TX_NODE_SIZE.height;
  const pathLines = Array.isArray(node.pathLabelLines) ? node.pathLabelLines : [];
  const pathText = pathLines.map((line, index) => `<tspan x="0" y="${(node.pathTextStartY || 0) + index * (node.pathLineHeight || 24)}">${escapeXml(line)}</tspan>`).join('');
  return [
    `<rect class="case-flow-visual-tx-rect" x="${-width / 2}" y="${-height / 2}" width="${width}" height="${height}" rx="16" ry="16"/>`,
    renderSvgFundsFollowedBar(node.fundsFollowedBar),
    node.amountText ? `<text class="case-flow-visual-amount-text" text-anchor="middle" y="-42">${escapeXml(node.amountText)}</text>` : '',
    `<text class="case-flow-visual-hash-text" text-anchor="middle" y="-8">${escapeXml(node.shortHash || '')}</text>`,
    node.dateText ? `<text class="case-flow-visual-date-text" text-anchor="middle" y="18">${escapeXml(node.dateText)}</text>` : '',
    node.chainText ? `<text class="case-flow-visual-chain-text" text-anchor="middle" y="42">${escapeXml(node.chainText)}</text>` : '',
    `<g transform="translate(0 ${height / 2 + 40})"><rect class="case-flow-visual-path-bg" x="${-(node.pathBoxWidth || 120) / 2}" y="${-(node.pathBoxHeight || 40) / 2}" width="${node.pathBoxWidth || 120}" height="${node.pathBoxHeight || 40}" rx="8" ry="8"/><text class="case-flow-visual-path-text" text-anchor="middle" font-size="${node.pathFontSize || 22}">${pathText}</text></g>`,
    node.badge ? `<text class="case-flow-visual-badge-text" text-anchor="middle" y="${-height / 2 - 14}">${escapeXml(node.badge)}</text>` : ''
  ].filter(Boolean).join('\n');
}

function renderSvgAddressNode(node = {}) {
  const labelLines = Array.isArray(node.labelLines) ? node.labelLines : [];
  const labels = labelLines.map((line, index) => {
    const y = -CASE_FLOW_VISUALS_ADDRESS_RADIUS - 26 - ((labelLines.length - 1 - index) * 20);
    const category = safeCssToken(node.labelCategory || 'regular');
    return `<text class="case-flow-visual-address-label case-flow-visual-address-label-${category}" text-anchor="middle" font-size="${node.labelFontSize || 16}" y="${y}">${escapeXml(line)}</text>`;
  }).join('\n');
  return [
    labels ? `<g>${labels}</g>` : '',
    `<circle class="case-flow-visual-address-circle" r="${CASE_FLOW_VISUALS_ADDRESS_RADIUS}"/>`,
    `<text class="case-flow-visual-address-text" text-anchor="middle" y="5">${escapeXml(node.shortAddress || '')}</text>`,
    Number(node.sourceCount) > 1 ? `<text class="case-flow-visual-address-count" text-anchor="middle" y="${CASE_FLOW_VISUALS_ADDRESS_RADIUS + 18}">×${escapeXml(node.sourceCount)}</text>` : ''
  ].filter(Boolean).join('\n');
}

function renderSvgFlowLabelNode(node = {}) {
  const boxWidth = node.boxWidth || 420;
  const boxHeight = node.boxHeight || 56;
  return [
    `<rect class="case-flow-visual-flow-label-bg" x="${-boxWidth / 2}" y="${-boxHeight / 2}" width="${boxWidth}" height="${boxHeight}" rx="14" ry="14"/>`,
    `<text class="case-flow-visual-flow-label-text" text-anchor="middle" font-size="${node.fontSize || 24}" y="9">${escapeXml(node.labelText || '')}</text>`
  ].join('\n');
}


function renderSvgAmountNode(node = {}) {
  const width = CASE_FLOW_VISUALS_AMOUNT_NODE_SIZE.width;
  const height = CASE_FLOW_VISUALS_AMOUNT_NODE_SIZE.height;
  return [
    `<rect class="case-flow-visual-amount-rect" x="${-width / 2}" y="${-height / 2}" width="${width}" height="${height}" rx="12" ry="12"/>`,
    `<text class="case-flow-visual-amount-node-text" text-anchor="middle" y="5">${escapeXml(node.amountText || '')}</text>`
  ].join('\n');
}

function renderSvgNode(node = {}, selectedNodeId = '') {
  const classes = caseFlowVisualNodeClass(node, selectedNodeId);
  let inner = '';
  if (node.type === 'tx') inner = renderSvgTxNode(node);
  else if (node.type === 'address') inner = renderSvgAddressNode(node);
  else if (node.type === 'amount') inner = renderSvgAmountNode(node);
  else if (node.type === 'flowLabel') inner = renderSvgFlowLabelNode(node);
  return `<g class="${escapeXml(classes)}" transform="translate(${node.x} ${node.y})">${inner}</g>`;
}

export function serializeCaseFlowVisualSvg(visual = {}, options = {}) {
  const nodes = Array.isArray(visual.nodes) ? visual.nodes : [];
  const edges = Array.isArray(visual.edges) ? visual.edges : [];
  const nodeLookup = new Map(nodes.map(node => [node.id, node]));
  const viewBox = visual.viewBox || { x: 0, y: 0, width: 1200, height: 700 };
  const title = options.title || `Case Buddy Flow ${visual.mainLabel || ''}`.trim();
  const description = options.description || 'Case Buddy Tool87-style flow visual generated from current Main Data and the reviewed address legend.';
  const edgeMarkup = edges.map((edge) => {
    const path = caseFlowVisualEdgePath(edge, nodeLookup);
    if (!path) return '';
    const marker = edge.dashed ? 'url(#case-flow-visual-arrow-muted)' : 'url(#case-flow-visual-arrow)';
    return `<path class="${escapeXml(caseFlowVisualEdgeClass(edge))}" d="${path}" marker-end="${marker}"/>`;
  }).filter(Boolean).join('\n');
  const nodeMarkup = nodes.map(node => renderSvgNode(node, options.selectedNodeId || '')).join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<svg xmlns="http://www.w3.org/2000/svg" width="${Math.ceil(viewBox.width)}" height="${Math.ceil(viewBox.height)}" viewBox="${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}" role="img" aria-label="${escapeXml(title)}">\n<title>${escapeXml(title)}</title>\n<desc>${escapeXml(description)}</desc>\n<style>${caseFlowVisualSvgStyles()}</style>\n<defs>\n<marker id="case-flow-visual-arrow" markerWidth="12" markerHeight="12" refX="10" refY="6" orient="auto" markerUnits="strokeWidth"><path class="case-flow-visual-arrow" d="M 0 0 L 12 6 L 0 12 z"/></marker>\n<marker id="case-flow-visual-arrow-muted" markerWidth="12" markerHeight="12" refX="10" refY="6" orient="auto" markerUnits="strokeWidth"><path class="case-flow-visual-arrow-muted" d="M 0 0 L 12 6 L 0 12 z"/></marker>\n</defs>\n<rect class="case-flow-visual-bg" x="${viewBox.x}" y="${viewBox.y}" width="${viewBox.width}" height="${viewBox.height}"/>\n<g class="case-flow-visual-edges">${edgeMarkup}</g>\n<g class="case-flow-visual-nodes">${nodeMarkup}</g>\n</svg>`;
}

export function safeCaseFlowVisualFilePart(value = '') {
  const cleaned = String(value || '')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return cleaned.slice(0, 80) || 'flow';
}

export function formatCaseFlowVisualTimestamp(date = new Date()) {
  const pad = num => String(num).padStart(2, '0');
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
    `${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
  ].join('-');
}

export async function rasterizeCaseFlowVisualSvg(svgText = '', format = 'png') {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('Raster export is only available in the browser.');
  }
  const mime = format === 'jpeg' || format === 'jpg' ? 'image/jpeg' : 'image/png';
  const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);
  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Could not render SVG for raster export.'));
      img.src = url;
    });
    const width = Math.max(800, image.naturalWidth || image.width || 1200);
    const height = Math.max(600, image.naturalHeight || image.height || 700);
    const scale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(width * scale);
    canvas.height = Math.ceil(height * scale);
    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return await new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) reject(new Error('Could not create raster export.'));
        else resolve(blob);
      }, mime, 0.94);
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function blobToUint8Array(blob) {
  const buffer = await blob.arrayBuffer();
  return new Uint8Array(buffer);
}

let crc32Table = null;

function buildCrc32Table() {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    table[n] = c >>> 0;
  }
  return table;
}

function crc32(bytes) {
  if (!crc32Table) crc32Table = buildCrc32Table();
  let crc = 0xffffffff;
  for (let index = 0; index < bytes.length; index += 1) {
    crc = (crc >>> 8) ^ crc32Table[(crc ^ bytes[index]) & 0xff];
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function dosDateTime(date = new Date()) {
  const year = Math.max(1980, date.getFullYear());
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = Math.floor(date.getSeconds() / 2);
  return {
    time: ((hours << 11) | (minutes << 5) | seconds) >>> 0,
    date: (((year - 1980) << 9) | (month << 5) | day) >>> 0
  };
}

function zipLocalFileHeader(nameBytes, size, crc, timestamp) {
  const header = new Uint8Array(30 + nameBytes.length);
  const view = new DataView(header.buffer);
  view.setUint32(0, 0x04034b50, true);
  view.setUint16(4, 20, true);
  view.setUint16(6, 0, true);
  view.setUint16(8, 0, true);
  view.setUint16(10, timestamp.time, true);
  view.setUint16(12, timestamp.date, true);
  view.setUint32(14, crc >>> 0, true);
  view.setUint32(18, size >>> 0, true);
  view.setUint32(22, size >>> 0, true);
  view.setUint16(26, nameBytes.length, true);
  view.setUint16(28, 0, true);
  header.set(nameBytes, 30);
  return header;
}

function zipCentralDirectoryHeader(nameBytes, size, crc, timestamp, offset) {
  const header = new Uint8Array(46 + nameBytes.length);
  const view = new DataView(header.buffer);
  view.setUint32(0, 0x02014b50, true);
  view.setUint16(4, 20, true);
  view.setUint16(6, 20, true);
  view.setUint16(8, 0, true);
  view.setUint16(10, 0, true);
  view.setUint16(12, timestamp.time, true);
  view.setUint16(14, timestamp.date, true);
  view.setUint32(16, crc >>> 0, true);
  view.setUint32(20, size >>> 0, true);
  view.setUint32(24, size >>> 0, true);
  view.setUint16(28, nameBytes.length, true);
  view.setUint16(30, 0, true);
  view.setUint16(32, 0, true);
  view.setUint16(34, 0, true);
  view.setUint16(36, 0, true);
  view.setUint32(38, 0, true);
  view.setUint32(42, offset >>> 0, true);
  header.set(nameBytes, 46);
  return header;
}

function zipEndOfCentralDirectory(fileCount, centralSize, centralOffset) {
  const header = new Uint8Array(22);
  const view = new DataView(header.buffer);
  view.setUint32(0, 0x06054b50, true);
  view.setUint16(4, 0, true);
  view.setUint16(6, 0, true);
  view.setUint16(8, fileCount, true);
  view.setUint16(10, fileCount, true);
  view.setUint32(12, centralSize >>> 0, true);
  view.setUint32(16, centralOffset >>> 0, true);
  view.setUint16(20, 0, true);
  return header;
}

function concatUint8Arrays(parts = []) {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const output = new Uint8Array(total);
  let offset = 0;
  parts.forEach((part) => {
    output.set(part, offset);
    offset += part.length;
  });
  return output;
}

export function createCaseFlowVisualZipBlob(files = []) {
  const encoder = new TextEncoder();
  const localParts = [];
  const centralParts = [];
  let offset = 0;
  const timestamp = dosDateTime(new Date());
  (Array.isArray(files) ? files : []).forEach((file) => {
    const nameBytes = encoder.encode(file.name || 'file.txt');
    let dataBytes;
    if (file.content instanceof Uint8Array) dataBytes = file.content;
    else dataBytes = encoder.encode(String(file.content || ''));
    const crc = crc32(dataBytes);
    const localHeader = zipLocalFileHeader(nameBytes, dataBytes.length, crc, timestamp);
    const centralHeader = zipCentralDirectoryHeader(nameBytes, dataBytes.length, crc, timestamp, offset);
    localParts.push(localHeader, dataBytes);
    centralParts.push(centralHeader);
    offset += localHeader.length + dataBytes.length;
  });
  const centralOffset = offset;
  const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);
  const endRecord = zipEndOfCentralDirectory(files.length, centralSize, centralOffset);
  return new Blob([concatUint8Arrays([...localParts, ...centralParts, endRecord])], { type: 'application/zip' });
}

export function buildCaseFlowVisualManifest(state = {}, visuals = [], format = 'svg') {
  return {
    app: 'Case Buddy Web App',
    source: 'Tool87-style in-app visual package',
    rendererVersion: CASE_FLOW_VISUALS_RENDERER_VERSION,
    generatedAt: new Date().toISOString(),
    format,
    caseDataRevision: state.caseDataRevision || 0,
    visualCount: visuals.length,
    recommendedSetup: {
      addressLabels: true,
      swapHighlights: true,
      bridgeHighlights: true,
      wayfinderColumnMerge: true,
      repeatedTransactionMerge: true,
      linearTrunkWrapMinNodes: 5,
      overlapCleanup: true
    },
    visuals: visuals.map(item => ({
      filename: item.filename,
      mainLabel: item.visual?.mainLabel || '',
      mainHash: item.visual?.mainHash || '',
      transactionNodesShown: item.visual?.stats?.transactionCount || 0,
      rawTransactionEntries: item.visual?.stats?.rawTransactionEntryCount || 0,
      mergedTransactionCount: item.visual?.stats?.mergedTransactionCount || 0,
      addressNodesShown: item.visual?.stats?.addressCount || 0,
      edgeCount: item.visual?.stats?.edgeCount || 0,
      wrappedTrunkLength: item.visual?.layoutStats?.wrappedTrunkLength || 0,
      mergedAddressNodes: item.visual?.layoutStats?.columnMergedNodes || 0,
      spacedNodes: item.visual?.layoutStats?.spacedAddressNodes || 0
    }))
  };
}

export function buildCaseFlowVisualBundleReadme(manifest = {}) {
  const lines = [];
  lines.push('Case Buddy Flow Visuals Package');
  lines.push('');
  lines.push(`Generated: ${manifest.generatedAt || ''}`);
  lines.push(`Renderer: ${manifest.rendererVersion || CASE_FLOW_VISUALS_RENDERER_VERSION}`);
  lines.push(`Format: ${String(manifest.format || 'svg').toUpperCase()}`);
  lines.push(`Visuals: ${manifest.visualCount || 0}`);
  lines.push('');
  lines.push('Recommended setup applied: address labels, swap/bridge highlights, WayFinder-style same-column address merge, repeated transaction-hash merge, linear trunk wrapping with a five-node minimum, and overlap cleanup.');
  lines.push('');
  lines.push('Files:');
  (manifest.visuals || []).forEach((visual, index) => {
    lines.push(`${index + 1}. ${visual.filename} — ${visual.mainLabel || 'Flow'} (${visual.transactionNodesShown || 0} tx node${(visual.transactionNodesShown || 0) === 1 ? '' : 's'}, ${visual.addressNodesShown || 0} address node${(visual.addressNodesShown || 0) === 1 ? '' : 's'})`);
  });
  return lines.join('\n');
}

export async function buildCaseFlowVisualBundleFiles(state = {}, options = {}) {
  const format = String(options.format || 'svg').toLowerCase() === 'jpg' ? 'jpeg' : String(options.format || 'svg').toLowerCase();
  const visualOptions = normalizeFlowOptions(options.visualOptions || options);
  const files = [];
  const visuals = [];
  const flows = Array.isArray(state.flows) ? state.flows : [];
  const onProgress = typeof options.onProgress === 'function' ? options.onProgress : null;

  for (let index = 0; index < flows.length; index += 1) {
    const flow = flows[index];
    if (onProgress) await onProgress({ index, total: flows.length, flow, phase: 'build' });
    const visual = buildCaseFlowVisual(flow, state, visualOptions);
    const baseName = `${String(index + 1).padStart(3, '0')}-${safeCaseFlowVisualFilePart(flow.mainLabel || 'flow')}-${safeCaseFlowVisualFilePart(caseFlowVisualsShortenMiddle(flow.mainHash || '', 10, 8) || 'no-hash')}`;
    const svgText = serializeCaseFlowVisualSvg(visual, {
      title: `Case Buddy Flow ${flow.mainLabel || index + 1}`,
      description: 'Auto-cleaned Case Buddy flow visual with Tool87-style recommended setup.'
    });
    let content = svgText;
    let extension = 'svg';
    if (format === 'png' || format === 'jpeg') {
      if (onProgress) await onProgress({ index, total: flows.length, flow, phase: 'rasterize' });
      const blob = await rasterizeCaseFlowVisualSvg(svgText, format);
      content = await blobToUint8Array(blob);
      extension = format === 'jpeg' ? 'jpg' : 'png';
    }
    const filename = `${baseName}.${extension}`;
    files.push({ name: filename, content });
    visuals.push({ filename, visual });
  }

  const manifest = buildCaseFlowVisualManifest(state, visuals, format === 'jpeg' ? 'jpg' : format);
  files.unshift({ name: 'README.txt', content: buildCaseFlowVisualBundleReadme(manifest) });
  files.unshift({ name: 'manifest.json', content: JSON.stringify(manifest, null, 2) });
  return { files, manifest };
}

export function downloadCaseFlowVisualBlob(content, filename, type) {
  if (typeof document === 'undefined') return false;
  const blob = content instanceof Blob ? content : new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return true;
}

export function buildCaseFlowVisualsPackageFilename(state = {}, format = 'svg') {
  const safeCase = safeCaseFlowVisualFilePart(state.caseDisplayId || state.caseDisplayName || 'case');
  return `case-buddy-flow-visuals-${safeCase}-${String(format || 'svg').toLowerCase()}-${formatCaseFlowVisualTimestamp(new Date())}.zip`;
}
